// ============================================================
// BEGIN CUSTOM EDIT: Length Guide Modal | Date: 2025-12-27
// What: Self-contained JS for simplified length guide modal on shoelace products
// ============================================================

(function() {
    'use strict';

    // Uses shared pfTrack/pfTrackStarted/pfTrackStep from stape-datalayer.js
    const LG_SOURCE = 'length_guide';
    const LG_STEP_TOTAL = 2;

    const LengthGuideModal = {
        config: null,
        preservedAglet: null,
        texts: null,
        modelsMap: null,
        initialized: false,
        currentContainer: null,
        // Event handler storage for cleanup
        _handlers: new Map(),
        // DOM element cache
        _domCache: new Map(),
        // Native scroll wrapper reference (replaces Swiper)
        brandSwiper: null,
        // Variant images map (variantId -> imageUrl)
        variantImages: null,
        // ============================================================
        // BEGIN CUSTOM EDIT: V2 API Data | Date: 2026-03-19
        // What: Store v2 API brands/models and Shopify product variants
        // ============================================================
        // V2 API data
        brands: null,
        brandMap: null,
        initialModels: null,
        // Shopify product variants for calculator flow
        productVariants: null,
        // Search debounce timer
        _searchDebounceTimer: null,
        // Infinite scroll state
        _infiniteScrollObserver: null,
        _infiniteScrollSentinel: null,
        _isLoadingMore: false,
        _currentSearchValue: null,
        modelsPagination: null,
        _initialPagination: null,
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        // ============================================================
        // BEGIN CUSTOM EDIT: Calculator State | Date: 2025-12-28
        // What: State properties for length calculator with simple/advanced modes
        // Updated: 2026-01-05 - Separate state per mode for isolated selections
        // ============================================================
        // Calculator state
        calculatorMode: 'simple',
        calculatorInputs: {
            shoeSize: null,
            shoeType: 'low-top',
            eyelets: null,
            verticalDistance: 5,
            horizontalDistance: 5,
            verticalDistanceCustom: false,
            horizontalDistanceCustom: false,
            verticalDistanceValue: '',
            horizontalDistanceValue: ''
        },
        // Separate saved inputs per mode for isolation
        simpleModeSavedInputs: null,
        advancedModeSavedInputs: null,
        calculatedLength: null,
        isCalculatorView: false,
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================


        // ============================================================
        // BEGIN CUSTOM EDIT: Tooltip Icon SVG | Date: 2026-01-04
        // What: SVG icon for tooltip hover (matches we-product-finder.js)
        // ============================================================
        TOOLTIP_ICON_SVG: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M6.06065 5.99992C6.21739 5.55436 6.52675 5.17866 6.93395 4.93934C7.34116 4.70002 7.81991 4.61254 8.28543 4.69239C8.75096 4.77224 9.17319 5.01427 9.47737 5.3756C9.78154 5.73694 9.94802 6.19427 9.94732 6.66658C9.94732 7.99992 7.94732 8.66658 7.94732 8.66658M8.00065 11.3333H8.00732M14.6673 7.99992C14.6673 11.6818 11.6825 14.6666 8.00065 14.6666C4.31875 14.6666 1.33398 11.6818 1.33398 7.99992C1.33398 4.31802 4.31875 1.33325 8.00065 1.33325C11.6825 1.33325 14.6673 4.31802 14.6673 7.99992Z" stroke="currentColor" stroke-opacity="0.5" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        // ============================================================
        // BEGIN CUSTOM EDIT: Stape DL – Helper | Date: 2026-03-20
        // What: DataLayer push helper and session tracking flags
        // ============================================================
        _dlStarted: false,
        _dlAddedToCart: false,
        _dlStartTime: null,
        _dlStepViewed: {},
        _dlLastStep: 'model_selection',
        _dlSearchTimer: null,
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        // ============================================================
        // BEGIN CUSTOM EDIT: Interaction Tracking | Date: 2026-03-19
        // What: Track model click (1) and select (3) events for dynamic ranking
        // ============================================================
        _trackedModelId: null,

        trackModelEvent(shoeModelId, eventType, source) {
            if (!shoeModelId || !eventType) return;
            const apiUrl = this._get(this.config, 'apiUrl', '');
            if (!apiUrl) return;
            try {
                fetch(`${apiUrl}/track-event`, {
                    method: 'POST',
                    body: JSON.stringify({
                        shoe_model_id: Number(shoeModelId),
                        event_type: eventType,
                        locale: typeof Shopify !== 'undefined' ? Shopify.locale : null,
                        source: source || null,
                        product_type: window.__senkelsProductType || null
                    }),
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'omit',
                    keepalive: true
                }).catch(() => {});
            } catch (e) {}
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        /**
         * Safely get nested property value (replaces optional chaining for older browsers)
         * @param {Object} obj - Object to access
         * @param {string} path - Dot-separated path (e.g., 'texts.backButton')
         * @param {*} defaultValue - Default value if path not found
         */
        _get(obj, path, defaultValue) {
            if (!obj || typeof path !== 'string') return defaultValue;
            const keys = path.split('.');
            let result = obj;
            for (const key of keys) {
                if (result == null || typeof result !== 'object') return defaultValue;
                result = result[key];
            }
            return result !== undefined ? result : defaultValue;
        },

        // ============================================================
        //  BEGIN CUSTOM EDIT: Imperial Unit Support | Date: 2026-03-20
        //  What: Auto-detect imperial locale and convert cm/mm to inches for display
        // ============================================================
        _isImperial() {
            return this._get(this.config, 'localization.unitSystem', '') === 'imperial';
        },

        _cmToInches(cm) {
            const CURATED = { 45: 18, 60: 24, 75: 30, 90: 35, 120: 47, 140: 55 };
            if (CURATED[cm] !== undefined) return CURATED[cm];
            return Math.round(cm * 0.393701 * 4) / 4;
        },

        _mmToInches(mm) {
            return Math.round(mm / 25.4 * 100) / 100;
        },

        _formatLength(cm) {
            return this._isImperial() ? this._cmToInches(cm) + '″' : cm + ' cm';
        },

        _formatSliderCm(cm) {
            return this._isImperial() ? (cm * 0.393701).toFixed(1) + '″' : cm + ' cm';
        },
        // ============================================================
        //  END CUSTOM EDIT
        // ============================================================

        /**
         * Sanitize text for safe HTML insertion (XSS prevention)
         * @param {string} text - Text to sanitize
         * @returns {string} - Sanitized text safe for innerHTML
         */
        _sanitize(text) {
            if (!text) return '';
            const div = document.createElement('div');
            div.textContent = String(text);
            return div.innerHTML;
        },

        // ============================================================
        //  BEGIN CUSTOM EDIT: Gift Box Template Helper | Date: 2026-05-08
        //  What: Read gift-box-option HTML from <template id="length-guide-gift-box-template">
        //        and substitute __GIFT_BOX_FORM_ID__ placeholder. Returns empty string when
        //        no gift-box product is configured (graceful no-op).
        // ============================================================
        _getGiftBoxOptionHtml(formId) {
            const tpl = document.getElementById('length-guide-gift-box-template');
            if (!tpl) return '';
            return tpl.innerHTML.split('__GIFT_BOX_FORM_ID__').join(formId);
        },
        // ============================================================
        //  END CUSTOM EDIT
        // ============================================================

        /**
         * Map a v2 API model object to the flat shape used for rendering.
         * Shared by renderModelSelector, rebuildModelsGrid and loadMoreModels.
         */
        _mapModelObj(modelObj) {
            var m = modelObj.model;
            if (!m || !m.brand_name || !m.silhouette) return null;
            var brand = this.brandMap ? this.brandMap.get(m.shoe_brand_id) : null;
            return {
                id: m.id,
                brand_name: m.brand_name,
                silhouette: m.silhouette,
                shoe_brand_id: m.shoe_brand_id,
                shopify_image_url: m.shopify_image_url || modelObj.shopify_image_url,
                brand_logo_url: brand ? brand.brand_logo_url : null,
                shoelace_variant_id: modelObj.shoelace_variant_id,
                custom_length: modelObj.custom_length
            };
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: V2 API Methods | Date: 2026-03-19
        // What: Fetch brands, models, variants from v2 API endpoints
        // ============================================================

        /**
         * Get current Shopify locale
         */
        getLocale() {
            return typeof Shopify !== 'undefined' ? Shopify.locale : 'en';
        },

        /**
         * Fetch brands from v2 API
         */
        async fetchBrands() {
            const apiUrl = this._get(this.config, 'apiUrl', '');
            const locale = this.getLocale();
            const pt = window.__senkelsProductType ? `&product_type=${encodeURIComponent(window.__senkelsProductType)}` : '';
            try {
                const res = await fetch(`${apiUrl}/brands?locale=${locale}${pt}`);
                if (!res.ok) throw new Error(`API error: ${res.status}`);
                const data = await res.json();
                return data.data || [];
            } catch (error) {
                console.error('Length guide: Error fetching brands:', error);
                return [];
            }
        },

        /**
         * Fetch models from v2 API (paginated, searchable)
         */
        async fetchModels(search = null, offset = 0) {
            const apiUrl = this._get(this.config, 'apiUrl', '');
            const locale = this.getLocale();
            const pt = window.__senkelsProductType ? `&product_type=${encodeURIComponent(window.__senkelsProductType)}` : '';
            let url = `${apiUrl}/models?locale=${locale}&offset=${offset}${pt}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            try {
                const res = await fetch(url);
                if (!res.ok) throw new Error(`API error: ${res.status}`);
                return await res.json();
            } catch (error) {
                console.error('Length guide: Error fetching models:', error);
                return { data: [], pagination: null };
            }
        },

        /**
         * Fetch models filtered by brand from v2 API
         */
        async fetchModelsByBrand(brandId, search = null) {
            const apiUrl = this._get(this.config, 'apiUrl', '');
            const locale = this.getLocale();
            const pt = window.__senkelsProductType ? `&product_type=${encodeURIComponent(window.__senkelsProductType)}` : '';
            let url = `${apiUrl}/models_by_brand?brand_id=${brandId}&locale=${locale}${pt}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            try {
                const res = await fetch(url);
                if (!res.ok) throw new Error(`API error: ${res.status}`);
                const data = await res.json();
                return data.data || [];
            } catch (error) {
                console.error('Length guide: Error fetching models by brand:', error);
                return [];
            }
        },

        /**
         * Fetch variant by ID from v2 API
         */
        async fetchVariantById(variantId) {
            const apiUrl = this._get(this.config, 'apiUrl', '');
            const locale = this.getLocale();
            try {
                const currency = this._get(this.config, 'localization.currency', '');
                // BEGIN CUSTOM EDIT: B2B Market Handle | Date: 2026-04-11 | What: Forward Shopify market handle to API for B2B pricing
                const market = this._get(this.config, 'localization.market', '');
                // END CUSTOM EDIT
                const res = await fetch(`${apiUrl}/getVariantByID?id=${variantId}&locale=${locale}&currency=${currency}&market=${encodeURIComponent(market || '')}`);
                if (!res.ok) throw new Error(`API error: ${res.status}`);
                const data = await res.json();
                return data.data || null;
            } catch (error) {
                console.error('Length guide: Error fetching variant:', error);
                return null;
            }
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: Variant Prefetch | Date: 2026-03-19
        // What: Prefetch variant data when model cards scroll into view (v2 pattern)
        // ============================================================
        _prefetchCache: new Map(),
        _prefetchQueue: [],
        _prefetchBatchSize: 5,
        _prefetchBatchTimeout: null,
        _prefetchObserver: null,

        _getPrefetchObserver() {
            if (!this._prefetchObserver && 'IntersectionObserver' in window) {
                this._prefetchObserver = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            const variantId = entry.target.dataset.prefetchVariantId;
                            if (variantId) this._queuePrefetch(variantId);
                            this._prefetchObserver.unobserve(entry.target);
                        }
                    });
                }, { rootMargin: '150px' });
            }
            return this._prefetchObserver;
        },

        _queuePrefetch(variantId) {
            if (!variantId || this._prefetchCache.has(variantId)) return;
            if (this._prefetchQueue.includes(variantId)) return;
            this._prefetchQueue.push(variantId);
            if (this._prefetchQueue.length >= this._prefetchBatchSize) {
                clearTimeout(this._prefetchBatchTimeout);
                this._flushPrefetchQueue();
            } else if (!this._prefetchBatchTimeout) {
                this._prefetchBatchTimeout = setTimeout(
                    () => this._flushPrefetchQueue(), 100
                );
            }
        },

        _flushPrefetchQueue() {
            this._prefetchBatchTimeout = null; // Reset so _queuePrefetch can schedule again
            if (this._prefetchQueue.length === 0) return;
            const batch = this._prefetchQueue.splice(0, this._prefetchBatchSize);
            batch.forEach(variantId => {
                if (this._prefetchCache.has(variantId)) return;
                const promise = this.fetchVariantById(variantId);
                this._prefetchCache.set(variantId, promise);
            });
            if (this._prefetchQueue.length > 0) {
                this._prefetchBatchTimeout = setTimeout(
                    () => this._flushPrefetchQueue(), 100
                );
            }
        },

        observeForPrefetch(element, variantId) {
            if (!variantId) return;
            element.dataset.prefetchVariantId = String(variantId);
            const observer = this._getPrefetchObserver();
            if (observer) observer.observe(element);
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        /**
         * Fetch product variants from Shopify AJAX API (for calculator flow)
         */
        async getProductVariants(handle) {
            try {
                const res = await fetch(`/products/${handle}.js`);
                const product = await res.json();
                return product.variants || [];
            } catch (e) {
                return [];
            }
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: V2 Calculator Fetch | Date: 2026-03-21
        // What: Fetch variants from v2 API by form+color for calculator flow
        // ============================================================
        async fetchVariantsByFormAndColor(formName, colorRelationName) {
            try {
                const apiUrl = this._get(this.config, 'apiUrl', '');
                const locale = this._get(this.config, 'localization.locale', 'en');
                const currency = this._get(this.config, 'localization.currency', '');
                // BEGIN CUSTOM EDIT: B2B Market Handle | Date: 2026-04-11 | What: Forward Shopify market handle to API for B2B pricing
                const market = this._get(this.config, 'localization.market', '');
                // END CUSTOM EDIT
                const res = await fetch(`${apiUrl}/getVariantsByFormAndColor?form_name=${encodeURIComponent(formName)}&color_relation_name=${encodeURIComponent(colorRelationName)}&locale=${locale}&currency=${currency}&market=${encodeURIComponent(market || '')}`);
                const data = await res.json();
                return data.data || [];
            } catch (e) {
                console.error('Failed to fetch variants by form and color:', e);
                return [];
            }
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        /**
         * Get current variant ID from page (form input or config)
         * @returns {string|null} - The current variant ID
         */
        getCurrentVariantId() {
            // Use config preset first (set by Liquid on main product page - most reliable)
            const configVariantId = this._get(this.config, 'presetValues.currentVariantId', null);
            if (configVariantId) {
                return String(configVariantId);
            }
            // Fall back to main product form input (use specific selector to avoid recommendation cards)
            const mainProductForm = document.querySelector('.product__info-wrapper form[action*="/cart/add"] input[name="id"]') ||
                                    document.querySelector('[data-product-form] input[name="id"]') ||
                                    document.querySelector('form.product-form input[name="id"]');
            if (mainProductForm && mainProductForm.value) {
                return mainProductForm.value;
            }
            return null;
        },

        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        /**
         * Store event handler reference for later cleanup
         */
        _storeHandler(element, event, handler) {
            const key = `${event}_${Date.now()}_${Math.random()}`;
            this._handlers.set(key, { element, event, handler });
            element.addEventListener(event, handler);
            return key;
        },

        /**
         * Remove all stored event handlers (call on cleanup)
         */
        _cleanupHandlers() {
            this._handlers.forEach(({ element, event, handler }) => {
                if (element && typeof element.removeEventListener === 'function') {
                    element.removeEventListener(event, handler);
                }
            });
            this._handlers.clear();
        },

        /**
         * Cache DOM element lookup
         */
        _cached(container, selector) {
            const cacheKey = `${selector}`;
            if (this._domCache.has(cacheKey)) {
                return this._domCache.get(cacheKey);
            }
            const element = container.querySelector(selector);
            if (element) {
                this._domCache.set(cacheKey, element);
            }
            return element;
        },

        /**
         * Clear DOM cache (call when container changes)
         */
        _clearCache() {
            this._domCache.clear();
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: Native CSS scroll cleanup | Date: 2026-03-19
        // What: Replace Swiper destroy with simple null reset (native scroll needs no cleanup)
        // ============================================================
        /**
         * Cleanup brand scroll wrapper reference (native CSS scroll, no Swiper)
         */
        _cleanupSwiper() {
            this.brandSwiper = null;
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        /**
         * Show view with fade transition
         */
        _showView(element, displayType = 'block') {
            if (!element) return;
            element.style.opacity = '0';
            element.style.display = displayType;
            element.offsetHeight; // reflow
            element.style.opacity = '1';
        },

        /**
         * Hide view with fade transition — returns Promise for sequential transitions
         */
        _hideView(element, callback) {
            if (!element) return Promise.resolve();
            element.style.opacity = '0';
            return new Promise(function(resolve) {
                setTimeout(function() {
                    element.style.display = 'none';
                    if (callback) callback();
                    resolve();
                }, 200);
            });
        },

        /**
         * Transition between two modal views with scoped slide + fade.
         * Animations are CSS-driven via .lg-slide-in-* / .lg-slide-out-* classes.
         */
        async _transitionViews(hideEl, showEl, displayType = 'block', direction = 'forward') {
            if (!hideEl && !showEl) return;

            const outClass = direction === 'back' ? 'lg-slide-out-right' : 'lg-slide-out-left';
            const inClass = direction === 'back' ? 'lg-slide-in-left' : 'lg-slide-in-right';
            const DURATION = 200;

            // Slide out the old view
            if (hideEl) {
                hideEl.classList.add(outClass);
                await new Promise(r => setTimeout(r, DURATION));
                hideEl.style.display = 'none';
                hideEl.classList.remove(outClass);
                hideEl.style.opacity = '';
            }

            // Slide in the new view
            if (showEl) {
                showEl.style.display = displayType;
                showEl.classList.add(inClass);
                // Force reflow so the animation plays
                showEl.offsetHeight;
                showEl.style.opacity = '1';
                await new Promise(r => setTimeout(r, DURATION));
                showEl.classList.remove(inClass);
            }
        },

        /**
         * Get skip option element (in drawer header, outside modal)
         */
        _getSkipOption(container) {
            // Skip option is in drawer header, not modal - traverse up to find it
            const drawer = container.closest('[data-length-guide-drawer]');
            return drawer ? drawer.querySelector('[data-skip-option]') : null;
        },

        /**
         * Initialize the modal
         */
        async init(container, config) {
            this.currentContainer = container;
            this.config = config || window.lengthGuideConfig;
            this.texts = this._get(this.config, 'texts', {});
            this.tooltipImages = this._get(this.config, 'tooltipImages', {});
            this.tooltipContent = this._get(this.config, 'tooltipContent', {});

            // Always get fresh aglet value when opening
            this.preservedAglet = this.getActiveAglet();

            // If already initialized with data, reset view to initial state
            if (this.initialized && this.brands && this.initialModels) {
                await this.resetView(container);
                return;
            }

            // Show loader for first-time initialization
            const loader = container.querySelector('[data-loader]');
            const content = container.querySelector('[data-content]');
            if (loader) loader.style.display = 'flex';
            if (content) content.style.display = 'none';

            // ============================================================
            // BEGIN CUSTOM EDIT: V2 API Init | Date: 2026-03-19
            // What: Fetch brands + initial models in parallel from v2 API
            // ============================================================
            const productHandle = this._get(this.config, 'presetValues.productHandle', null);
            const [brandsData, modelsResponse, shopifyVariants] = await Promise.all([
                this.fetchBrands(),
                this.fetchModels(),
                productHandle ? this.getProductVariants(productHandle) : Promise.resolve([])
            ]);

            this.brands = brandsData;
            this.initialModels = modelsResponse.data || [];
            this.modelsPagination = modelsResponse.pagination || null;
            this._initialPagination = modelsResponse.pagination || null;
            this.productVariants = shopifyVariants;

            // Build brandMap for O(1) logo lookups by brand id
            this.brandMap = new Map();
            this.brands.forEach(function(brand) {
                this.brandMap.set(brand.id, brand);
            }.bind(this));

            // Get untranslated aglet from v2 API using current variant
            const currentVariantId = this.getCurrentVariantId();
            if (currentVariantId) {
                const variantData = await this.fetchVariantById(currentVariantId);
                if (variantData && variantData.option_1) {
                    this.preservedAglet = variantData.option_1;
                    // ============================================================
                    // BEGIN CUSTOM EDIT: V2 Calculator Form+Color | Date: 2026-03-21
                    // What: Store v2 form_name + color_relation_name for calculator flow
                    // ============================================================
                    this._v2FormName = variantData.form_name || null;
                    this._v2ColorRelation = variantData.color_relation_name || null;
                    this._v2Variants = null; // reset so next open re-fetches
                    // ============================================================
                    // END CUSTOM EDIT
                    // ============================================================
                }
            }
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            if ((!this.brands || this.brands.length === 0) && (!this.initialModels || this.initialModels.length === 0)) {
                const errorText = this._get(this, 'texts.errorLoading', 'Error loading data.');
                if (loader) {
                    loader.textContent = '';
                    var errorP = document.createElement('p');
                    errorP.textContent = errorText;
                    loader.appendChild(errorP);
                }
                return;
            }

            // Render model selection UI
            this.renderModelSelector(container);
            this.initialized = true;

            // ============================================================
            // BEGIN CUSTOM EDIT: Skip to Calculator Handler | Date: 2025-12-28
            // What: Setup skip button to show calculator view directly
            // ============================================================
            // Skip to calculator buttons - check both container and drawer header
            const drawer = container.closest('[data-length-guide-drawer]');
            const skipButtons = [
                ...container.querySelectorAll('[data-skip-to-calculator]'),
                ...(drawer ? drawer.querySelectorAll('[data-skip-to-calculator]') : [])
            ];
            skipButtons.forEach(btn => {
                this._storeHandler(btn, 'click', () => this.showCalculatorView(container));
            });
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================
        },

        /**
         * Reset view to model selection (for reopening modal)
         */
        async resetView(container) {
            // ============================================================
            // BEGIN CUSTOM EDIT: Cleanup handlers on reset | Date: 2025-12-28
            // What: Prevent event listener accumulation on modal reopen
            // ============================================================
            this._cleanupHandlers();
            this._clearCache();
            this.cleanupInfiniteScroll();
            this._currentSearchValue = null;
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            // ============================================================
            // BEGIN CUSTOM EDIT: V2 untranslated aglet on reopen | Date: 2026-03-19
            // What: Re-lookup untranslated aglet via v2 API when modal reopens
            // Why: preservedAglet gets set to translated DOM value (e.g. "Latón")
            //      but API uses English values (e.g. "Brass")
            // ============================================================
            const currentVariantId = this.getCurrentVariantId();
            if (currentVariantId) {
                const variantData = await this.fetchVariantById(currentVariantId);
                if (variantData && variantData.option_1) {
                    this.preservedAglet = variantData.option_1;
                    // ============================================================
                    // BEGIN CUSTOM EDIT: V2 Calculator Form+Color Reset | Date: 2026-03-21
                    // What: Re-store v2 form_name + color_relation_name on modal reopen
                    // ============================================================
                    this._v2FormName = variantData.form_name || null;
                    this._v2ColorRelation = variantData.color_relation_name || null;
                    this._v2Variants = null; // reset so next open re-fetches
                    // ============================================================
                    // END CUSTOM EDIT
                    // ============================================================
                }
            }
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            const modelsView = container.querySelector('[data-models-view]');
            const resultView = container.querySelector('[data-result-view]');
            const calculatorView = container.querySelector('[data-calculator-view]');
            const skipOption = this._getSkipOption(container);

            // Show models view and skip option, hide result and calculator views
            if (modelsView) {
                modelsView.style.display = 'block';
                modelsView.style.opacity = '1';
            }
            if (skipOption) {
                skipOption.style.display = 'flex';
                skipOption.style.opacity = '1';
            }
            if (resultView) {
                resultView.style.display = 'none';
                resultView.style.opacity = '0';
                resultView.innerHTML = ''; // Clear result content
            }
            if (calculatorView) {
                calculatorView.style.display = 'none';
                calculatorView.style.opacity = '0';
            }

            // Reset calculator state
            this.isCalculatorView = false;
            this.calculatedLength = null;
            this.calculatorMode = 'simple';  // Reset to simple mode
            this.simpleModeSavedInputs = null;  // Clear saved mode states
            this.advancedModeSavedInputs = null;
            this.calculatorInputs = {
                shoeSize: null,
                shoeType: 'low-top',
                eyelets: null,
                verticalDistance: 5,
                horizontalDistance: 5,
                verticalDistanceCustom: false,
                horizontalDistanceCustom: false,
                verticalDistanceValue: '',
                horizontalDistanceValue: ''
            };

            // Reset any selected models
            const selectedRadios = container.querySelectorAll('input[type="radio"]:checked');
            selectedRadios.forEach(radio => radio.checked = false);

            // Reset brand selection
            this.selectedBrand = null;
            const brandLabel = container.querySelector('[data-brand-label]');
            if (brandLabel) brandLabel.textContent = '';

            // Show all model cards (clear brand filter)
            const modelsGrid = container.querySelector('[data-models-grid]');
            if (modelsGrid) {
                modelsGrid.querySelectorAll('[data-model-select]').forEach(card => {
                    card.style.display = '';
                });
            }

            // Clear search input
            const searchInput = container.querySelector('[data-model-search]');
            if (searchInput) searchInput.value = '';

            // ============================================================
            // BEGIN CUSTOM EDIT: Re-attach handlers after reset | Date: 2025-12-28
            // What: Handlers were cleaned up, need to re-attach for modal to work
            // ============================================================
            if (modelsView) {
                this.setupBrandSelection(modelsView);
                this.setupSearch(modelsView);
                this.setupModelSelection(modelsView, container);
            }

            // Re-attach skip to calculator handlers - check both container and drawer header
            const drawer = container.closest('[data-length-guide-drawer]');
            const skipButtons = [
                ...container.querySelectorAll('[data-skip-to-calculator]'),
                ...(drawer ? drawer.querySelectorAll('[data-skip-to-calculator]') : [])
            ];
            skipButtons.forEach(btn => {
                this._storeHandler(btn, 'click', () => this.showCalculatorView(container));
            });
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================
        },

        /**
         * Full reset for different product (clears cached data)
         */
        fullReset() {
            // Cleanup event handlers and Swiper to prevent memory leaks
            this._cleanupHandlers();
            this._cleanupSwiper();
            this._clearCache();
            this.cleanupInfiniteScroll();
            this._currentSearchValue = null;
            this.modelsPagination = null;
            this._initialPagination = null;

            this.brands = null;
            this.brandMap = null;
            this.initialModels = null;
            this.productVariants = null;
            this.modelsMap = null;
            this.allModels = null;
            this.sortedBrands = null;
            this.selectedBrand = null;
            this.initialized = false;
            this.currentContainer = null;

            // Reset calculator state
            this.calculatorMode = 'simple';
            this.simpleModeSavedInputs = null;
            this.advancedModeSavedInputs = null;
            this.calculatorInputs = {
                shoeSize: null,
                shoeType: 'low-top',
                eyelets: null,
                verticalDistance: 5,
                horizontalDistance: 5,
                verticalDistanceCustom: false,
                horizontalDistanceCustom: false,
                verticalDistanceValue: '',
                horizontalDistanceValue: ''
            };
            this.calculatedLength = null;
            this.isCalculatorView = false;
        },

        /**
         * Get current Aglet from product page form
         */
        getActiveAglet() {
            // Try checked radio button (most common pattern)
            const radio = document.querySelector(
                'form[action*="/cart/add"] input[name="option1"]:checked, ' +
                'form[action*="/cart/add"] input[data-option-position="1"]:checked, ' +
                '.product-form input[name="option1"]:checked'
            );
            if (radio) return radio.value;

            // Try select dropdown
            const select = document.querySelector(
                'form[action*="/cart/add"] select[name="option1"], ' +
                'variant-picker select:first-of-type, ' +
                '.product-form select[data-option-position="1"]'
            );
            if (select) return select.value;

            // Try hidden variant input and fetch variant data
            const variantInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
            const variantId = variantInput ? variantInput.value : null;
            if (variantId && window.productVariants) {
                const variant = window.productVariants.find(v => String(v.id) === String(variantId));
                return variant && variant.option1 ? variant.option1 : null;
            }

            // Try Impact theme variant picker
            const variantPicker = document.querySelector('variant-picker');
            if (variantPicker) {
                const selectedVariant = variantPicker.selectedVariant;
                if (selectedVariant) return selectedVariant.option1;
            }

            return null;
        },

        /**
         * Check if value represents custom length (multi-language support)
         */
        isCustomLength(value, sku) {
            // SKU-based detection: all custom length variants have "-C-" in SKU
            if (sku && String(sku).includes('-C-')) return true;
            // Fallback: translated option value check
            if (!value) return false;
            const lower = String(value).toLowerCase();
            return lower === 'wunschlänge' ||
                   lower === 'custom' ||
                   lower === 'custom length' ||
                   lower.includes('wunsch') ||
                   lower.includes('custom') ||
                   lower.includes('personnalisé') ||
                   lower.includes('sur mesure') ||
                   lower.includes('mesure') ||
                   lower.includes('personalizada') ||
                   lower.includes('personalizzata') ||
                   lower.includes('zwyczaj') ||
                   lower.includes('szokás') ||
                   lower.includes('tilpasset');
        },

        /**
         * Extract handle from variant_url
         * e.g., "https://handmade-shoelace.myshopify.com/products/sapphire-blue-4mm-round?variant=..." -> "sapphire-blue-4mm-round"
         */
        extractHandleFromUrl(url) {
            if (!url) return null;
            const match = url.match(/\/products\/([^?/]+)/);
            return match ? match[1] : null;
        },

        /**
         * Render model selector UI - matches product finder structure exactly
         */
        renderModelSelector(container) {
            const loader = container.querySelector('[data-loader]');
            const content = container.querySelector('[data-content]');
            const modelsView = container.querySelector('[data-models-view]');

            // ============================================================
            // BEGIN CUSTOM EDIT: V2 API Model Rendering | Date: 2026-03-19
            // What: Use v2 API models directly — preserve server-side ranking order
            // Updated: 2026-03-19 — Removed dedup Map; v2 /api/models returns unique models pre-sorted by productfinder_ranking
            // ============================================================
            // Map v2 API model objects to flat shape for rendering
            // v2 shape: { model: { id, brand_name, silhouette, shoe_brand_id, shopify_image_url, productfinder_ranking }, shopify_image_url, shoelace_variant_id, custom_length }
            var uniqueModels = (this.initialModels || []).map(this._mapModelObj.bind(this)).filter(Boolean);

            // Use v2 brands API data (order from API)
            var sortedBrands = (this.brands || []).slice();

            // Store for filtering — models keep API ranking order
            this.allModels = uniqueModels;
            this.sortedBrands = sortedBrands;
            this.selectedBrand = null;

            // Build modelsMap for click handler lookup (brand_name|silhouette -> model)
            this.modelsMap = new Map();
            uniqueModels.forEach(function(m) {
                this.modelsMap.set(m.brand_name + '|' + m.silhouette, m);
            }.bind(this));
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            // Render UI matching product finder structure
            // Note: Section header is in drawer header slot (variant-picker.liquid)
            modelsView.innerHTML = `
                <div class="product-finder-section" data-section-index="model">
                    <div class="section-content">
                        <!-- Search input - matches product finder -->
                        <div class="product-finder-search">
                            <div class="product-finder-search-container">
                                <svg class="product-finder-search-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <path d="M21 21l-4.35-4.35"></path>
                                </svg>
                                <input type="text"
                                       class="product-finder-search-input"
                                       placeholder="${this._get(this, 'texts.searchPlaceholder', 'Search brand or model...')}"
                                       data-model-search>
                                <button type="button" class="product-finder-search-clear" data-search-clear aria-label="Clear search" style="display: none;">
                                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M18 6L6 18M6 6l12 12"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Brand selector - horizontal swiper like product finder -->
                        <div class="option-selector" data-option-name="brand_name" data-parent-property="model">
                            <label>
                                ${this._get(this, 'texts.brandLabel', 'Brand')}<span class="length-guide-brand-count" data-brand-count> (${Number(sortedBrands.length).toLocaleString(this._get(this.config, 'locale', 'en'))})</span> <span class="selected-value" data-brand-label></span>
                                <div class="swiper-nav-container">
                                    <button type="button" class="swiper-button-prev-custom" aria-label="Previous">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                    <button type="button" class="swiper-button-next-custom" aria-label="Next">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                </div>
                            </label>
                            <div class="swiper radio-option-circles-swiper" data-brand-swiper>
                                <div class="swiper-wrapper">
                                    ${sortedBrands.map(brand => {
                                        const brandName = brand.name;
                                        const logoUrl = brand.brand_logo_url || null;
                                        const brandId = brand.id;
                                        return `
                                            <div class="swiper-slide">
                                                <div class="radio-option-circle" data-brand-select="${brandName}" data-brand-id="${brandId}">
                                                    <input type="radio" id="brand-${brandName.replace(/\s+/g, '-').toLowerCase()}" name="brand_name" value="${brandName}">
                                                    <label for="brand-${brandName.replace(/\s+/g, '-').toLowerCase()}">
                                                        <div class="radio-option-circle-content">
                                                            ${logoUrl
                                                                ? `<img src="${logoUrl}" alt="${brandName}" loading="lazy">`
                                                                : `<span class="radio-option-circle-text">${brandName}</span>`
                                                            }
                                                        </div>
                                                        <span class="radio-option-circle-title">${brandName}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        `;
                                    }).join('')}
                                </div>
                            </div>
                        </div>

                        <!-- Model grid - matches product finder model-name-grid -->
                        <div class="option-selector" data-option-name="name" data-parent-property="model">
                            <div class="radio-option-values model-name-grid grid-cols-3" data-models-grid role="listbox" aria-label="${this._get(this, 'texts.stepTitle', 'Select your shoe')}">
                                ${this.renderModelCards(uniqueModels)}
                            </div>
                        </div>

                        <!-- Empty state -->
                        <div class="product-finder-empty-state" style="display: none;" data-empty-state>
                            <p>${this._get(this, 'texts.emptyTitle', 'Model not found?')}</p>
                            <p>${this._get(this, 'texts.emptyDescriptionCalculator', 'Calculate your required length using our proprietary lace length calculator.')}</p>
                            <button type="button" class="btn we-btn length-guide-skip-button" data-skip-to-calculator>
                                ${this._get(this, 'texts.calculateButton', 'Calculate')}
                            </button>
                        </div>
                    </div>
                </div>
            `;

            // Hide loader, show content
            if (loader) loader.style.display = 'none';
            if (content) content.style.display = 'block';

            // Update preload status for CSS
            container.setAttribute('data-preload-status', 'ready');

            // Initialize Swiper for brand selector
            this.initBrandSwiper(modelsView);

            // Setup search functionality
            this.setupSearch(modelsView);

            // Setup brand selection
            this.setupBrandSelection(modelsView);

            // Setup model selection
            this.setupModelSelection(modelsView, container);

            // Setup infinite scroll for paginated models
            var modelsGridForScroll = modelsView.querySelector('[data-models-grid]');
            if (modelsGridForScroll) {
                this.setupInfiniteScroll(modelsGridForScroll);
            }

            // ============================================================
            // BEGIN CUSTOM EDIT: Stape DL – pf_step_viewed | Date: 2026-03-20
            // What: Fire step viewed for model_selection (once per session)
            // ============================================================
            pfTrackStep(LG_SOURCE, 1, 'model_selection', LG_STEP_TOTAL);
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================
        },

        /**
         * Render model cards in product finder format
         * v2 model shape stored in modelsMap: { id, brand_name, silhouette, shoe_brand_id, shopify_image_url, brand_logo_url, shoelace_variant_id, custom_length }
         */
        renderModelCards(models) {
            return models.map(model => {
                // Clean silhouette - remove brand prefix if present
                let displayName = model.silhouette || '';
                if (model.brand_name && displayName.toLowerCase().startsWith(model.brand_name.toLowerCase())) {
                    displayName = displayName.substring(model.brand_name.length).trim();
                }
                const brandLogoUrl = model.brand_logo_url || '';

                return `
                    <div class="radio-option-image" data-model-select="${model.brand_name}|${model.silhouette}" data-brand="${model.brand_name}" data-brand-id="${model.shoe_brand_id || ''}">
                        <input type="radio" id="model-${(model.brand_name + '-' + model.silhouette).replace(/\s+/g, '-').toLowerCase()}" name="model_name" value="${model.silhouette}" data-model-id="${model.id || ''}" data-variant-id="${model.shoelace_variant_id || ''}" data-custom-length="${model.custom_length || ''}">
                        <label for="model-${(model.brand_name + '-' + model.silhouette).replace(/\s+/g, '-').toLowerCase()}">
                            <div class="radio-option-image-container">
                                ${model.shopify_image_url
                                    ? `<img src="${model.shopify_image_url}" alt="${displayName}" loading="lazy">`
                                    : `<div class="radio-option-image-placeholder">
                                         <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5">
                                           <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                                         </svg>
                                       </div>`
                                }
                            </div>
                            <div class="length-guide-model-footer">
                                <div class="radio-option-image-text-container">
                                    <span class="radio-option-image-title">${model.brand_name}</span>
                                    <span class="radio-option-image-silhouette">${displayName}</span>
                                </div>
                                ${brandLogoUrl
                                    ? `<img src="${brandLogoUrl}" alt="${model.brand_name}" class="length-guide-model-brand-logo" loading="lazy">`
                                    : ''
                                }
                            </div>
                        </label>
                    </div>
                `;
            }).join('');
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: Native CSS scroll brand carousel | Date: 2026-03-19
        // What: Replace Swiper (67KB) with native CSS scroll (0KB, matches v2 product finder)
        // ============================================================
        /**
         * Initialize native scroll for brand carousel (replaces Swiper)
         */
        initBrandSwiper(modelsView) {
            const swiperContainer = modelsView.querySelector('[data-brand-swiper]');
            const prevBtn = modelsView.querySelector('.swiper-button-prev-custom');
            const nextBtn = modelsView.querySelector('.swiper-button-next-custom');

            if (!swiperContainer) return;

            // Store wrapper reference for scroll updates
            this.brandSwiper = swiperContainer.querySelector('.swiper-wrapper');

            if (prevBtn) {
                this._storeHandler(prevBtn, 'click', () => {
                    const wrapper = swiperContainer.querySelector('.swiper-wrapper');
                    const slideWidth = wrapper.querySelector('.swiper-slide')?.offsetWidth || 80;
                    wrapper.scrollBy({ left: -slideWidth * 2, behavior: 'smooth' });
                });
            }

            if (nextBtn) {
                this._storeHandler(nextBtn, 'click', () => {
                    const wrapper = swiperContainer.querySelector('.swiper-wrapper');
                    const slideWidth = wrapper.querySelector('.swiper-slide')?.offsetWidth || 80;
                    wrapper.scrollBy({ left: slideWidth * 2, behavior: 'smooth' });
                });
            }
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        /**
         * Setup brand selection handlers using event delegation
         */
        setupBrandSelection(modelsView) {
            const brandLabel = modelsView.querySelector('[data-brand-label]');
            const modelsGrid = modelsView.querySelector('[data-models-grid]');
            const brandSwiper = modelsView.querySelector('[data-brand-swiper]');

            if (!brandSwiper) return;

            // ============================================================
            // BEGIN CUSTOM EDIT: Use _storeHandler for cleanup | Date: 2025-12-28
            // What: Store event handler reference to prevent accumulation
            // ============================================================
            // Use event delegation on swiper container for more reliable clicks
            this._storeHandler(brandSwiper, 'click', async (e) => {
                // Find the brand-select element (could be target or ancestor)
                const brandEl = e.target.closest('[data-brand-select]');
                if (!brandEl) return;

                e.preventDefault();
                e.stopPropagation();

                const brandName = brandEl.dataset.brandSelect;
                // ============================================================
                // BEGIN CUSTOM EDIT: Parse brandId as number | Date: 2026-03-19
                // What: API expects numeric brand_id, dataset returns string
                // ============================================================
                const rawBrandId = brandEl.dataset.brandId;
                const brandId = rawBrandId ? parseInt(rawBrandId, 10) : null;
                if (!brandId && brandId !== 0) {
                    console.warn('Length guide: Missing brand ID for', brandName);
                    return;
                }
                // ============================================================
                // END CUSTOM EDIT
                // ============================================================
                const radioInput = brandEl.querySelector('input[type="radio"]');

                // Uncheck all other radios first
                brandSwiper.querySelectorAll('input[type="radio"]').forEach(r => r.checked = false);

                // If clicking same brand, deselect (show all models)
                if (this.selectedBrand === brandName) {
                    this.selectedBrand = null;
                    this.selectedBrandId = null;
                    if (brandLabel) brandLabel.textContent = '';
                    // Reset to initial models from API
                    await this.filterModelsByBrand(modelsGrid, null);
                } else {
                    // Select new brand - fetch models from API
                    this.selectedBrand = brandName;
                    this.selectedBrandId = brandId;
                    if (radioInput) radioInput.checked = true;
                    if (brandLabel) brandLabel.textContent = brandName;
                    await this.filterModelsByBrand(modelsGrid, brandName, brandId);
                    // ============================================================
                    // BEGIN CUSTOM EDIT: Stape DL – pf_selection_made | Date: 2026-03-20
                    // What: Fire brand selection event
                    // ============================================================
                    pfTrack('pf_selection_made', LG_SOURCE, { selection_type: 'brand', value: brandName });
                    // ============================================================
                    // END CUSTOM EDIT
                    // ============================================================
                }
            });
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: V2 API Brand Filter | Date: 2026-03-19
        // What: Fetch models by brand from API instead of client-side filter
        // ============================================================
        /**
         * Filter model cards by brand via v2 API
         */
        async filterModelsByBrand(modelsGrid, brandName, brandId) {
            if (!modelsGrid) return;

            // Brand-filtered results are not paginated — cleanup infinite scroll
            this.cleanupInfiniteScroll();

            if (!brandName) {
                // No brand selected - rebuild from initial models
                await this.rebuildModelsGrid(modelsGrid, this.initialModels);
                // Restore original pagination and re-enable infinite scroll
                this.modelsPagination = this._initialPagination || null;
                this.setupInfiniteScroll(modelsGrid);
                return;
            }

            // Fetch models for this brand from API
            var models = await this.fetchModelsByBrand(brandId);

            if (!models || models.length === 0) {
                // Reset in-memory state so stale data isn't used on next interaction
                this.allModels = [];
                this.modelsMap = new Map();
                // Show empty state — API may have returned 500 or no models for this brand
                var emptyState = modelsGrid.closest('.section-content').querySelector('[data-empty-state]');
                modelsGrid.innerHTML = '';
                if (emptyState) emptyState.style.display = 'flex';
                return;
            }

            // Hide empty state if visible
            var emptyState = modelsGrid.closest('.section-content').querySelector('[data-empty-state]');
            if (emptyState) emptyState.style.display = 'none';

            // Rebuild the grid with filtered models (no pagination)
            await this.rebuildModelsGrid(modelsGrid, models);
        },

        /**
         * Rebuild models grid from v2 API model array
         */
        // ============================================================
        // BEGIN CUSTOM EDIT: Fade transition for model grid | Date: 2026-03-19
        // What: Smooth fade out/in when switching brands (matches v2)
        // ============================================================
        async rebuildModelsGrid(modelsGrid, models, skipFadeOut) {
            if (!modelsGrid) return;

            // Fade out existing content (skipped when caller already handled it, e.g. search spinner)
            if (!skipFadeOut) {
                modelsGrid.style.transition = 'opacity 0.15s ease-out';
                modelsGrid.style.opacity = '0';
                await new Promise(function(resolve) { setTimeout(resolve, 150); });
            }

            // Map v2 API models directly — preserve server-side ranking order
            var uniqueModels = (models || []).map(this._mapModelObj.bind(this)).filter(Boolean);
            this.allModels = uniqueModels;

            // Rebuild modelsMap for click handler lookup
            this.modelsMap = new Map();
            uniqueModels.forEach(function(m) {
                this.modelsMap.set(m.brand_name + '|' + m.silhouette, m);
            }.bind(this));

            modelsGrid.innerHTML = this.renderModelCards(uniqueModels);

            // ============================================================
            // BEGIN CUSTOM EDIT: Variant Prefetch Wire | Date: 2026-03-19
            // What: Observe rendered model cards for intersection-based prefetch
            // ============================================================
            var self = this;
            modelsGrid.querySelectorAll('.radio-option-image').forEach(function(card, index) {
                var model = uniqueModels[index];
                if (model && model.shoelace_variant_id) {
                    self.observeForPrefetch(card, model.shoelace_variant_id);
                }
            });
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            // Handle empty state
            var sectionContent = modelsGrid.closest('.section-content');
            var emptyState = sectionContent ? sectionContent.querySelector('[data-empty-state]') : null;
            if (emptyState) {
                emptyState.style.display = uniqueModels.length === 0 ? 'block' : 'none';
            }

            // Fade in new content
            requestAnimationFrame(function() {
                modelsGrid.style.transition = 'opacity 0.2s ease-in';
                modelsGrid.style.opacity = '1';
            });
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        // ============================================================
        // BEGIN CUSTOM EDIT: Infinite Scroll | Date: 2026-03-19
        // What: Paginated model loading via IntersectionObserver sentinel (matches v2)
        // ============================================================

        /**
         * Setup infinite scroll sentinel for paginated model loading
         */
        setupInfiniteScroll(modelsGrid) {
            // Clean up existing
            this.cleanupInfiniteScroll();

            if (!this.modelsPagination || !this.modelsPagination.hasMore) return;

            // Create sentinel
            this._infiniteScrollSentinel = document.createElement('div');
            this._infiniteScrollSentinel.style.height = '1px';
            modelsGrid.appendChild(this._infiniteScrollSentinel);

            this._infiniteScrollObserver = new IntersectionObserver(function(entries) {
                entries.forEach(function(entry) {
                    if (entry.isIntersecting && !this._isLoadingMore && this.modelsPagination && this.modelsPagination.hasMore) {
                        this._isLoadingMore = true;
                        this.loadMoreModels(modelsGrid);
                    }
                }.bind(this));
            }.bind(this), { rootMargin: '100px', threshold: 0.1 });

            this._infiniteScrollObserver.observe(this._infiniteScrollSentinel);
        },

        /**
         * Cleanup infinite scroll observer and sentinel
         */
        cleanupInfiniteScroll() {
            if (this._infiniteScrollObserver) {
                this._infiniteScrollObserver.disconnect();
                this._infiniteScrollObserver = null;
            }
            if (this._infiniteScrollSentinel) {
                this._infiniteScrollSentinel.remove();
                this._infiniteScrollSentinel = null;
            }
            this._isLoadingMore = false;
        },

        /**
         * Load next page of models and append to grid
         */
        async loadMoreModels(modelsGrid) {
            if (!this.modelsPagination || !this.modelsPagination.nextOffset) {
                this._isLoadingMore = false;
                return;
            }

            try {
                var response = await this.fetchModels(this._currentSearchValue || null, this.modelsPagination.nextOffset);
                var newModels = response.data || [];
                this.modelsPagination = response.pagination || null;

                if (newModels.length > 0) {
                    // Map to flat shape
                    var mappedModels = newModels.map(this._mapModelObj.bind(this)).filter(Boolean);

                    // Add to allModels and modelsMap
                    this.allModels = this.allModels.concat(mappedModels);
                    mappedModels.forEach(function(m) {
                        this.modelsMap.set(m.brand_name + '|' + m.silhouette, m);
                    }.bind(this));

                    // Remove sentinel before appending
                    if (this._infiniteScrollSentinel) this._infiniteScrollSentinel.remove();

                    // Append new cards (don't replace existing)
                    var newCardsHtml = this.renderModelCards(mappedModels);
                    modelsGrid.insertAdjacentHTML('beforeend', newCardsHtml);

                    // Wire prefetch for new cards
                    var self = this;
                    var newCards = modelsGrid.querySelectorAll('.radio-option-image');
                    var startIndex = newCards.length - mappedModels.length;
                    for (var i = startIndex; i < newCards.length; i++) {
                        var model = mappedModels[i - startIndex];
                        if (model && model.shoelace_variant_id) {
                            self.observeForPrefetch(newCards[i], model.shoelace_variant_id);
                        }
                    }

                }
            } catch (error) {
                console.error('Length guide: Error loading more models:', error);
            } finally {
                this._isLoadingMore = false;
                // Re-setup infinite scroll if more pages (must run even on error to restore sentinel)
                if (this.modelsPagination && this.modelsPagination.hasMore) {
                    this.setupInfiniteScroll(modelsGrid);
                }
            }
        },

        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        /**
         * Setup search functionality - works with new product finder structure
         */
        setupSearch(modelsView) {
            const searchInput = modelsView.querySelector('[data-model-search]');
            const modelsGrid = modelsView.querySelector('[data-models-grid]');
            const brandSwiper = modelsView.querySelector('[data-brand-swiper]');
            const emptyState = modelsView.querySelector('[data-empty-state]');
            const clearButton = modelsView.querySelector('[data-search-clear]');

            if (!searchInput) return;

            // BEGIN CUSTOM EDIT: Clear button handler | Date: 2026-01-07
            // What: Add click handler for clear button
            if (clearButton) {
                this._storeHandler(clearButton, 'click', () => {
                    searchInput.value = '';
                    clearButton.style.display = 'none';
                    searchInput.dispatchEvent(new Event('input', { bubbles: true }));
                    searchInput.focus();
                });
            }
            // END CUSTOM EDIT

            // ============================================================
            // BEGIN CUSTOM EDIT: V2 API Search with Debounce | Date: 2026-03-19
            // What: Search models via v2 API with 300ms debounce
            // ============================================================
            this._storeHandler(searchInput, 'input', (e) => {
                const searchValue = e.target.value.trim();

                // Show/hide clear button
                if (clearButton) {
                    clearButton.style.display = e.target.value ? 'flex' : 'none';
                }

                // ============================================================
                // BEGIN CUSTOM EDIT: Stape DL – pf_search | Date: 2026-03-20
                // What: Fire search event with 500ms debounce, min 2 chars
                // ============================================================
                if (this._dlSearchTimer) clearTimeout(this._dlSearchTimer);
                if (searchValue.length >= 2) {
                    this._dlSearchTimer = setTimeout(() => {
                        pfTrack('pf_search', LG_SOURCE, { search_term: searchValue });
                    }, 500);
                }
                // ============================================================
                // END CUSTOM EDIT
                // ============================================================

                // Clear existing debounce timer
                if (this._searchDebounceTimer) {
                    clearTimeout(this._searchDebounceTimer);
                }

                // Debounce API call by 300ms
                this._searchDebounceTimer = setTimeout(async () => {
                    // Store search value for infinite scroll pagination
                    this._currentSearchValue = searchValue || null;
                    this.cleanupInfiniteScroll();

                    if (!searchValue) {
                        // Empty search - reset to initial models and show all brands
                        await this.rebuildModelsGrid(modelsGrid, this.initialModels);
                        // Restore original pagination for unfiltered list and re-enable scroll
                        this.modelsPagination = this._initialPagination || null;
                        this.setupInfiniteScroll(modelsGrid);
                        if (brandSwiper) {
                            brandSwiper.querySelectorAll('.swiper-slide').forEach(slide => {
                                slide.style.display = '';
                                slide.style.visibility = '';
                            });
                        }
                        // BEGIN CUSTOM EDIT: Brand Count Badge | Date: 2026-05-28 | What: Reset to total when search cleared
                        var totalBrandBadge = modelsView.querySelector('[data-brand-count]');
                        if (totalBrandBadge) {
                            var lgmLocaleReset = this._get(this.config, 'locale', 'en');
                            var totalBrands = Array.isArray(this.sortedBrands) ? this.sortedBrands.length : (Array.isArray(this.brands) ? this.brands.length : 0);
                            totalBrandBadge.textContent = ' (' + Number(totalBrands).toLocaleString(lgmLocaleReset) + ')';
                        }
                        // END CUSTOM EDIT
                        return;
                    }

                    // Fade out and show spinner while fetching (matches v2 fluid feel)
                    modelsGrid.style.transition = 'opacity 0.15s ease-out';
                    modelsGrid.style.opacity = '0';
                    await new Promise(function(r) { setTimeout(r, 150); });
                    modelsGrid.innerHTML = '<div class="length-guide-grid-spinner"></div>';
                    modelsGrid.style.opacity = '1';

                    // Fetch models matching search from API
                    var response = await this.fetchModels(searchValue);
                    var models = response.data || [];
                    this.modelsPagination = response.pagination || null;

                    // Rebuild models grid with search results (skip fade-out — spinner already visible)
                    await this.rebuildModelsGrid(modelsGrid, models, true);

                    // Setup infinite scroll for search results pagination
                    this.setupInfiniteScroll(modelsGrid);

                    // Filter brand swiper to show only brands with matching models
                    if (brandSwiper) {
                        var matchingBrandIds = new Set();
                        models.forEach(function(modelObj) {
                            if (modelObj.model && modelObj.model.shoe_brand_id) {
                                matchingBrandIds.add(String(modelObj.model.shoe_brand_id));
                            }
                        });

                        var slides = brandSwiper.querySelectorAll('.swiper-slide');
                        var matchingCount = 0;
                        slides.forEach(function(slide) {
                            var brandEl = slide.querySelector('[data-brand-select]');
                            var slideBrandId = brandEl ? brandEl.dataset.brandId : '';
                            var hasMatchingModels = matchingBrandIds.has(String(slideBrandId));
                            slide.style.display = hasMatchingModels ? '' : 'none';
                            slide.style.visibility = hasMatchingModels ? '' : 'hidden';
                            if (hasMatchingModels) matchingCount++;
                        });

                        // If no brands match, show all brands
                        if (matchingCount === 0) {
                            slides.forEach(function(slide) {
                                slide.style.display = '';
                                slide.style.visibility = '';
                            });
                        }

                        // BEGIN CUSTOM EDIT: Brand Count Badge | Date: 2026-05-28 | What: Update count to filtered brand count (or total when fallback shows all)
                        var filteredBrandBadge = modelsView.querySelector('[data-brand-count]');
                        if (filteredBrandBadge) {
                            var lgmLocaleFiltered = this._get(this.config, 'locale', 'en');
                            var totalBrandsLgm = Array.isArray(this.sortedBrands) ? this.sortedBrands.length : (Array.isArray(this.brands) ? this.brands.length : 0);
                            var displayedBrandCount = matchingCount > 0 ? matchingCount : totalBrandsLgm;
                            filteredBrandBadge.textContent = ' (' + Number(displayedBrandCount).toLocaleString(lgmLocaleFiltered) + ')';
                        }
                        // END CUSTOM EDIT

                    }
                }, 300);
            });
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================
        },

        /**
         * Setup model selection handlers - works with radio-option-image cards
         */
        setupModelSelection(modelsView, container) {
            const modelsGrid = modelsView.querySelector('[data-models-grid]');
            if (!modelsGrid) return;

            // ============================================================
            // BEGIN CUSTOM EDIT: Use event delegation for cleanup | Date: 2025-12-28
            // What: Single delegated handler instead of per-card handlers
            // ============================================================
            this._storeHandler(modelsGrid, 'click', (e) => {
                // Ignore the synthetic click fired by <label> → <input> association
                // (label click already bubbles here; without this guard every click fires twice)
                if (e.target.tagName === 'INPUT') return;

                const card = e.target.closest('.radio-option-image');
                if (!card) return;

                const modelKey = card.dataset.modelSelect;
                const selectedModel = this.modelsMap.get(modelKey);
                const radioInput = card.querySelector('input[type="radio"]');

                if (selectedModel) {
                    // Check the radio input
                    if (radioInput) radioInput.checked = true;

                    const modelId = selectedModel.id;
                    this._trackedModelId = modelId;

                    // ============================================================
                    // BEGIN CUSTOM EDIT: Stape DL – pf_selection_made | Date: 2026-03-20
                    // What: Fire model selection event with brand + silhouette
                    // ============================================================
                    pfTrack('pf_selection_made', LG_SOURCE, { selection_type: 'model', value: selectedModel.brand_name + ' ' + selectedModel.silhouette, model_id: modelId });
                    // ============================================================
                    // END CUSTOM EDIT
                    // ============================================================

                    // Process selection via v2 API (Flow A)
                    this.onModelSelect(selectedModel, container);
                }
            });
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: V2 Model Selection Fix | Date: 2026-03-24
        // What: Use shoelace_variant_id only for recommended length; resolve correct
        //       variant for current product + current aglet via findVariantWithTolerance
        // Why: shoelace_variant_id points to a generic variant (wrong product/aglet);
        //      must match against current product's variants like calculator flow does
        // ============================================================
        /**
         * Handle model selection - resolves correct variant for current product + aglet
         */
        async onModelSelect(modelObject, container) {
            const variantId = modelObject.shoelace_variant_id;

            if (!variantId) {
                this.showError(container, modelObject);
                return;
            }

            // Step 1: Fetch the model's reference variant to get the recommended length
            let refVariantData = this._prefetchCache.get(String(variantId));
            if (refVariantData instanceof Promise) refVariantData = await refVariantData;
            if (!refVariantData) refVariantData = await this.fetchVariantById(variantId);

            if (!refVariantData) {
                this.showError(container, modelObject);
                return;
            }

            // Determine the recommended length from the reference variant or model data
            const refIsCustom = refVariantData.is_custom || this.isCustomLength(refVariantData.option_2, refVariantData.sku);
            const recommendedLength = refIsCustom
                ? parseFloat(String(modelObject.custom_length || '0').replace(/[^\d.]/g, ''))
                : (refVariantData.length_cm || parseInt(String(refVariantData.option_2 || '').replace(/[^\d]/g, '')));

            if (!recommendedLength) {
                this.showError(container, modelObject);
                return;
            }

            // Step 2: Lazy-load current product's variants (same as calculator flow)
            if (!this._v2Variants && this._v2FormName && this._v2ColorRelation) {
                this._v2Variants = await this.fetchVariantsByFormAndColor(this._v2FormName, this._v2ColorRelation);
            }

            // Step 3: Prefer the exact fixed reference variant when it belongs to the current product set.
            // This prevents fixed recommendations such as Clarks Desert Boot Evo (45 cm) from falling through to custom.
            const directVariant = refIsCustom ? null : this.findVariantById(variantId);
            const result = directVariant
                ? { variant: directVariant, matchedLength: this.getVariantLength(directVariant) || recommendedLength, isCustom: false }
                : this.findVariantWithTolerance(recommendedLength, this.productVariants, this.preservedAglet);

            let resolvedVariant = null;
            let isCustom = false;
            let lengthValue = recommendedLength;

            if (result.variant) {
                resolvedVariant = result.variant;
                isCustom = result.isCustom;
                lengthValue = result.matchedLength;
            } else if (!refIsCustom) {
                // Fixed recommendations should not silently become custom.
                const closestResult = this.findVariantWithTolerance(recommendedLength, this.productVariants, this.preservedAglet, { preferClosestStandard: true });
                if (closestResult.variant) {
                    resolvedVariant = closestResult.variant;
                    isCustom = false;
                    lengthValue = closestResult.matchedLength;
                }
            } else {
                // No match via tolerance — try custom length variant for true custom recommendations
                const customVariant = this.findCustomLengthVariant();
                if (customVariant) {
                    resolvedVariant = customVariant;
                    isCustom = true;
                    lengthValue = recommendedLength;
                }
            }

            if (!resolvedVariant) {
                this.showError(container, modelObject);
                return;
            }

            // Store the CORRECT variant (current product + aglet) on modelObject
            modelObject._variantData = resolvedVariant;

            // Show result
            await this.showResult(container, modelObject, lengthValue, isCustom);
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        /**
         * Show result view with length and add to cart
         * Matches product finder's product-summary-card structure exactly
         */
        async showResult(container, modelObject, lengthValue, isCustom) {
            const modelsView = container.querySelector('[data-models-view]');
            const resultView = container.querySelector('[data-result-view]');

            if (!resultView) return;

            const brandName = modelObject.brand_name || '';
            let silhouette = modelObject.silhouette || modelObject.name || '';
            // Remove duplicate brand name from silhouette if present
            if (brandName && silhouette.toLowerCase().startsWith(brandName.toLowerCase())) {
                silhouette = silhouette.substring(brandName.length).trim();
            }
            const productTitle = this._get(this.config, 'presetValues.productTitle', '');

            // ============================================================
            // BEGIN CUSTOM EDIT: V2 Variant Data for Result | Date: 2026-03-19
            // What: Use v2 API variant data from onModelSelect instead of findVariantFromAPI
            // ============================================================
            const variant = modelObject._variantData || null;

            const formName = variant ? (variant.form_name || '') : '';
            const formLabel = this._get(this, 'texts.formLabel', 'Form');
            const isV2Variant = variant && !!variant.variant_id;
            const priceInCents = variant ? (isV2Variant ? Math.round((variant.price || 0) * 100) : (variant.price || 0)) : 0;
            const priceNote = this._get(this, 'texts.priceNote', 'inkl. MwSt. Kostenloser Versand ab 2 Paar');
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            // Get VARIANT image based on selected aglet color
            // Look up in variantImages map (keyed by option1/aglet value)
            let variantImageUrl = '';
            const variantImages = this._get(this.config, 'variantImages', {});

            if (this.preservedAglet && variantImages[this.preservedAglet]) {
                // Found variant image for this aglet color
                variantImageUrl = variantImages[this.preservedAglet];
            } else {
                // Fallback to featured product image
                variantImageUrl = this._get(this.config, 'presetValues.productImage', '');
            }

            // Format display text (avoid double "cm" if value already includes it)
            const formatLength = (val) => {
                const num = parseFloat(String(val).replace(/[^\d.]/g, ''));
                if (this._isImperial() && !isNaN(num)) {
                    return this._cmToInches(num) + '″';
                }
                const str = String(val).trim();
                return str.toLowerCase().endsWith('cm') ? str : `${str} cm`;
            };
            const customDisplayLength = modelObject.custom_length || lengthValue;
            const displayLength = isCustom
                ? formatLength(customDisplayLength)
                : formatLength(lengthValue);

            // Get model image for result display
            const modelImageUrl = modelObject.shopify_image_url || '';

            // BEGIN CUSTOM EDIT: B2B Form ID | Date: 2026-04-11 | What: Unique form id linking modal qty input to volume pricing table and addToCart
            this._currentFormId = `lg-modal-form-${(variant && variant.variant_id) || (variant && variant.id) || 'unknown'}-${Date.now()}`;
            // END CUSTOM EDIT

            resultView.innerHTML = `
                <div class="length-guide-result">
                    <button type="button" class="length-guide-result__back" data-back aria-label="Back to selection" role="button">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        ${this._get(this, 'texts.backButton', 'Back to selection')}
                    </button>

                    <!-- Product summary card - matches product finder structure -->
                    <div class="product-summary-card">
                        <!-- Variant image (shoelace) on left -->
                        ${variantImageUrl ? `
                            <div class="product-summary-image-container">
                                <img src="${variantImageUrl}" alt="${productTitle}" class="product-summary-image" loading="lazy">
                            </div>
                        ` : ''}

                        <!-- Info container on right -->
                        <div class="product-summary-info">
                            <!-- Model section with "Perfect laces for" -->
                            <div class="product-summary-recommended-model">
                                ${modelImageUrl
                                    ? `<img src="${modelImageUrl}" alt="${brandName} ${silhouette}" class="product-summary-recommended-model-image" loading="lazy">`
                                    : ''
                                }
                                <div class="product-summary-recommended-model-text">
                                    <span class="product-summary-recommended-model-title">${this._get(this, 'texts.perfectLacesFor', 'Perfect laces for')}</span>
                                    <span class="product-summary-recommended-model-name">${this._sanitize(brandName)} ${this._sanitize(silhouette)}</span>
                                </div>
                            </div>

                            <!-- Product title -->
                            ${productTitle ? `<div class="product-summary-title">${this._sanitize(productTitle)}</div>` : ''}

                            <!-- Details grid - 2 columns -->
                            <div class="product-summary-detail-container">
                                ${formName ? `
                                    <div class="product-summary-detail">
                                        <span class="product-summary-detail-label">${formLabel}</span>
                                        <span class="product-summary-detail-value">${this._sanitize(formName)}</span>
                                    </div>
                                ` : ''}
                                ${this.preservedAglet ? `
                                    <div class="product-summary-detail">
                                        <span class="product-summary-detail-label">${this._get(this, 'texts.agletLabel', 'Aglet')}</span>
                                        <span class="product-summary-detail-value">${this._sanitize(this.preservedAglet)}</span>
                                    </div>
                                ` : ''}
                                <div class="product-summary-detail product-summary-detail--length">
                                    <span class="product-summary-detail-label">${this._get(this, 'texts.lengthLabel', 'Length')}</span>
                                    <span class="product-summary-detail-value">${this._sanitize(displayLength)}</span>
                                </div>
                            </div>

                            <!-- Price container (hidden for B2B — volume table already shows per-tier pricing) -->
                            ${!this._b2bGetDefaults().enabled && priceInCents > 0 ? `
                                <div class="product-summary-price-container">
                                    <span class="product-summary-price">${this._formatMoney(priceInCents)}</span>
                                    <span class="product-summary-price-note">${priceNote}</span>
                                </div>
                            ` : ''}
                            ${this._renderB2BSummary(variant, this._currentFormId)}

                            <!-- Add to cart button -->
                            <div class="length-guide-result__actions">
                                <button type="button" class="length-guide-result__add-to-cart" data-add-to-cart aria-label="${this._get(this, 'texts.addToCartFull', 'In den Warenkorb')}" role="button">
                                    ${this._get(this, 'texts.addToCartFull', 'In den Warenkorb')}
                                </button>
                            </div>

                            <!-- BEGIN CUSTOM EDIT: Gift Box below ATC (model flow) | Date: 2026-05-08 | What: Render gift-box checkbox below the ATC actions row; addToCart() detects checkbox via [data-gift-box-variant]:checked and switches to items[]/parent_id payload -->
                            ${this._getGiftBoxOptionHtml(this._currentFormId)}
                            <!-- END CUSTOM EDIT -->
                        </div>
                    </div>
                </div>
            `;

            // Hide models and skip option FIRST, then show result (prevents both views visible at once)
            const skipOption = this._getSkipOption(container);
            await Promise.all([
                this._hideView(modelsView),
                this._hideView(skipOption)
            ]);
            this._showView(resultView, 'block');
            // BEGIN CUSTOM EDIT: Wire active-tier highlight | Date: 2026-05-14
            this._b2bSetupActiveTier(resultView, this._currentFormId);
            // END CUSTOM EDIT

            // ============================================================
            // BEGIN CUSTOM EDIT: Interaction Tracking | Date: 2026-03-19
            // What: Track model select event (type 3) when result renders
            // ============================================================
            this.trackModelEvent(this._trackedModelId, 3, 'length_guide');
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            // ============================================================
            // BEGIN CUSTOM EDIT: Stape DL – pf_result_displayed | Date: 2026-03-20
            // What: Fire when model result view is shown
            // ============================================================
            this._dlLastStep = 'result';
            pfTrack('pf_result_displayed', LG_SOURCE, { variant_id: modelObject.shoelace_variant_id || '', shoe_model: (modelObject.brand_name || '') + ' ' + (modelObject.silhouette || ''), shoelace_title: '' });
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            // Back button handler with transition
            const backBtn = resultView.querySelector('[data-back]');
            if (backBtn) {
                const backHandler = () => {
                    this._hideView(resultView);
                    this._showView(modelsView, 'block');
                    this._showView(skipOption, 'flex');
                };
                this._storeHandler(backBtn, 'click', backHandler);
            }

            // Add to cart handler with stored reference for cleanup
            const addToCartBtn = resultView.querySelector('[data-add-to-cart]');
            if (addToCartBtn) {
                const cartHandler = async () => {
                    await this.addToCart(addToCartBtn, modelObject, lengthValue, isCustom, container);
                };
                this._storeHandler(addToCartBtn, 'click', cartHandler);
            }
        },

        /**
         * Add to cart with cart drawer integration
         */
        async addToCart(button, modelObject, lengthValue, isCustom, container) {
            // Show loading state - use theme native button loader structure
            button.disabled = true;
            button.setAttribute('aria-busy', 'true');
            const originalText = button.textContent;

            // Create theme-native button structure with loader
            button.innerHTML = `
                <div class="length-guide-btn-content">${originalText}</div>
                <span class="button__loader">
                    <span></span>
                    <span></span>
                    <span></span>
                </span>
            `;

            try {
                // ============================================================
                // BEGIN CUSTOM EDIT: V2 Add to Cart | Date: 2026-03-19
                // What: Use v2 variant data from modelObject._variantData (Flow A)
                // ============================================================
                // Use variant data resolved in onModelSelect (v2 API or Shopify AJAX)
                const variant = modelObject._variantData;
                let useCustomFallback = false;

                if (!variant || !(variant.variant_id || variant.id)) {
                    throw new Error('Variant not found');
                }
                // ============================================================
                // END CUSTOM EDIT
                // ============================================================

                const brandName = modelObject.brand_name || '';
                const silhouette = modelObject.silhouette || modelObject.name || '';
                const shoeLabel = this._get(this, 'texts.shoeLabel', 'Shoe');

                // Gather sections to request (variant-added + cart drawer if exists)
                let sectionsToBundle = ['variant-added'];
                document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
                    bubbles: true,
                    detail: { sections: sectionsToBundle }
                }));

                // BEGIN CUSTOM EDIT: B2B Qty Read | Date: 2026-04-13 | What: Read and normalize quantity from in-modal qty selector instead of hardcoded 1
                const modalQty = this._readModalQuantity(this._currentFormId, 1, variant);
                // END CUSTOM EDIT

                // Build parent line-item properties (assigned in either single-item or items[] payload)
                const parentProperties = {
                    '_Source': 'Length Guide',
                    [shoeLabel]: `${brandName} ${silhouette}`.trim()
                };

                // Add custom length property with ACTUAL value from model or shoe_mapping
                // Use translated label from config (line-item.liquid adds the unit)
                const customLengthLabel = this._get(this, 'texts.customLengthLabel', 'Custom Length');

                if (isCustom) {
                    const customLen = modelObject.custom_length || lengthValue;
                    const numericValue = parseFloat(String(customLen).replace(/[^\d.]/g, ''));
                    if (!isNaN(numericValue)) {
                        parentProperties[customLengthLabel] = this._isImperial() ? this._cmToInches(numericValue) : numericValue;
                    }
                }

                // ============================================================
                // BEGIN CUSTOM EDIT: Gift Box Detection (model flow) | Date: 2026-05-08
                // What: When gift-box checkbox is ticked, switch single cartData to items[]
                //       payload with parent_id so gift-box renders as nested cart line.
                // ============================================================
                const resultViewEl = container.querySelector('[data-result-view]');
                const giftBoxInput = resultViewEl?.querySelector('input[data-gift-box-variant]:checked');
                const giftBoxVariantId = giftBoxInput ? parseInt(giftBoxInput.dataset.giftBoxVariant, 10) : null;
                const parentVariantId = parseInt(variant.variant_id || variant.id, 10);

                let cartData;
                if (giftBoxVariantId && Number.isFinite(giftBoxVariantId) && Number.isFinite(parentVariantId)) {
                    cartData = {
                        items: [
                            { id: parentVariantId, quantity: modalQty, properties: parentProperties },
                            { id: giftBoxVariantId, quantity: modalQty, parent_id: parentVariantId }
                        ],
                        sections: sectionsToBundle.join(','),
                        sections_url: window.location.pathname
                    };
                } else {
                    cartData = {
                        id: variant.variant_id || variant.id,
                        quantity: modalQty,
                        properties: parentProperties,
                        sections: sectionsToBundle.join(','),
                        sections_url: window.location.pathname
                    };
                }
                // ============================================================
                // END CUSTOM EDIT
                // ============================================================

                const shopifyRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
                const response = await fetch(`${shopifyRoot}cart/add.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify(cartData)
                });

                if (!response.ok) {
                    throw new Error(`Cart add failed: ${response.status}`);
                }

                const responseJson = await response.json();

                // ============================================================
                // BEGIN CUSTOM EDIT: Stape DL – pf_add_to_cart | Date: 2026-03-20
                // What: Fire ATC event on model flow success
                // ============================================================
                this._dlAddedToCart = true;
                pfTrack('pf_add_to_cart', LG_SOURCE, { variant_id: variant.variant_id || '', is_custom_length: !!isCustom, shoe_model: '' });
                // ============================================================
                // END CUSTOM EDIT
                // ============================================================

                // Fetch updated cart for cart:change event
                const cartResponse = await fetch(`${shopifyRoot}cart.js`);
                const cartContent = await cartResponse.json();

                // Merge sections from add response into cart content
                if (responseJson.sections) {
                    cartContent.sections = responseJson.sections;
                }

                // ============================================================
                // BEGIN CUSTOM EDIT: Cart Integration Fix | Date: 2025-12-28
                // What: Wait for drawer close, add missing items property, add cart:error event
                // ============================================================

                // Close the length guide drawer and dispatch events AFTER it's fully closed
                const drawer = container.closest('x-drawer');
                if (drawer && drawer.hide) {
                    // Wait for drawer to fully hide before dispatching cart events
                    drawer.addEventListener('dialog:after-hide', () => {
                        // Dispatch variant:add event with items property (required by theme)
                        document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
                            bubbles: true,
                            detail: {
                                items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
                                cart: cartContent
                            }
                        }));

                        // Also dispatch cart:change for other listeners (cart count, etc.)
                        if (cartContent.sections && Object.keys(cartContent.sections).length > 1) {
                            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                                bubbles: true,
                                detail: {
                                    baseEvent: 'variant:add',
                                    cart: cartContent
                                }
                            }));
                        } else {
                            document.dispatchEvent(new CustomEvent('cart:refresh'));
                        }
                    }, { once: true });

                    drawer.hide();
                } else {
                    // Fallback: dispatch events immediately if no drawer
                    document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
                        bubbles: true,
                        detail: {
                            items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
                            cart: cartContent
                        }
                    }));
                }

                // Reset view in background (so it's ready for next open)
                setTimeout(() => {
                    this.resetView(container);
                }, 300);

                // ============================================================
                // END CUSTOM EDIT
                // ============================================================

            } catch (error) {

                // Dispatch cart:error event for theme error handling
                button.dispatchEvent(new CustomEvent('cart:error', {
                    bubbles: true,
                    detail: {
                        error: error.message || 'Unable to add item to cart'
                    }
                }));

                button.removeAttribute('aria-busy');
                const errorText = this._get(this, 'texts.addToCartError', 'Error - Try again');
                button.innerHTML = `<div class="length-guide-btn-content">${errorText}</div>`;
                button.classList.remove('success');
                button.disabled = false;
            }
        },

        /**
         * Show error when no length is found
         */
        showError(container, modelObject) {
            const modelsView = container.querySelector('[data-models-view]');
            const resultView = container.querySelector('[data-result-view]');

            const brandName = modelObject.brand_name || '';
            let silhouette = modelObject.silhouette || '';
            // Remove duplicate brand name from silhouette if present
            if (brandName && silhouette.toLowerCase().startsWith(brandName.toLowerCase())) {
                silhouette = silhouette.substring(brandName.length).trim();
            }
            const modelImageUrl = modelObject.shopify_image_url || '';
            const productTitle = this._get(this.config, 'presetValues.productTitle', '');

            resultView.innerHTML = `
                <div class="length-guide-result length-guide-result--error">
                    <button type="button" class="length-guide-result__back" data-back aria-label="Back to selection" role="button">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        ${this._get(this, 'texts.backButton', 'Back to selection')}
                    </button>

                    <!-- Product summary card - matches product finder structure -->
                    <div class="product-summary-card">
                        <div class="product-summary-info">
                            <!-- Model section -->
                            <div class="product-summary-recommended-model">
                                ${modelImageUrl
                                    ? `<img src="${modelImageUrl}" alt="${brandName} ${silhouette}" class="product-summary-recommended-model-image" loading="lazy">`
                                    : ''
                                }
                                <div class="product-summary-recommended-model-text">
                                    <span class="product-summary-recommended-model-title">${this._get(this, 'texts.perfectLacesFor', 'Perfect laces for')}</span>
                                    <span class="product-summary-recommended-model-name">${this._sanitize(brandName)} ${this._sanitize(silhouette)}</span>
                                </div>
                            </div>

                            ${productTitle ? `<div class="product-summary-title">${this._sanitize(productTitle)}</div>` : ''}

                            <div class="length-guide-result__error-title">${this._get(this, 'texts.errorTitle', 'Length not available')}</div>

                            <div class="length-guide-result__message">
                                <p>${this._get(this, 'texts.errorMessage1', '') || `No standard length is available for <strong>${this._sanitize(brandName)} ${this._sanitize(silhouette)}</strong>.`}</p>
                                <p>${this._get(this, 'texts.errorMessage2', 'Use the length calculator to find the right length.')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            `;

            const skipOption = this._getSkipOption(container);
            this._hideView(modelsView);
            this._hideView(skipOption);
            this._showView(resultView, 'block');

            // Back button handler with transition
            const backBtn = resultView.querySelector('[data-back]');
            if (backBtn) {
                const backHandler = () => {
                    this._hideView(resultView);
                    this._showView(modelsView, 'block');
                    this._showView(skipOption, 'flex');
                };
                this._storeHandler(backBtn, 'click', backHandler);
            }
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: Calculator Methods | Date: 2025-12-28
        // What: Methods for length calculator matching Product Finder logic
        // ============================================================

        // Length step options - returns translated options using config
        getLengthStepOptions() {
            const calcTexts = this._get(this, 'texts.calculator', {});
            const vd = this._get(calcTexts, 'verticalDistance', {});
            const hd = this._get(calcTexts, 'horizontalDistance', {});
            const eyeletTexts = this._get(calcTexts, 'eyelets', {});
            const pairSingular = eyeletTexts.pair || 'Pair';
            const pairPlural = eyeletTexts.pairs || 'Pairs';

            return {
                verticalDistance: [
                    { title: this._get(vd, 'narrow.title', 'Narrow'), description: this._get(vd, 'narrow.description', '2-3 cm'), value: 3 },
                    { title: this._get(vd, 'medium.title', 'Medium'), description: this._get(vd, 'medium.description', 'approx. 5 cm'), value: 5 },
                    { title: this._get(vd, 'wide.title', 'Wide'), description: this._get(vd, 'wide.description', 'approx. 7 cm'), value: 7 }
                ],
                horizontalDistance: [
                    { title: this._get(hd, 'narrow.title', 'Narrow'), description: this._get(hd, 'narrow.description', '2-3 cm'), value: 3 },
                    { title: this._get(hd, 'medium.title', 'Medium'), description: this._get(hd, 'medium.description', 'approx. 5 cm'), value: 5 },
                    { title: this._get(hd, 'wide.title', 'Wide'), description: this._get(hd, 'wide.description', 'approx. 7 cm'), value: 7 }
                ],
                numberOfEylets: [
                    { title: '1 ' + pairSingular, value: 1 },
                    { title: '2 ' + pairPlural, value: 2 },
                    { title: '3 ' + pairPlural, value: 3 },
                    { title: '4 ' + pairPlural, value: 4 },
                    { title: '5 ' + pairPlural, value: 5 },
                    { title: '6 ' + pairPlural, value: 6 },
                    { title: '7 ' + pairPlural, value: 7 },
                    { title: '8 ' + pairPlural, value: 8 },
                    { title: '9 ' + pairPlural, value: 9 },
                    { title: '10 ' + pairPlural, value: 10 }
                ]
            };
        },

        // Show calculator view with View Transition
        async showCalculatorView(container) {
            const modelsView = container.querySelector('[data-models-view]');
            const calculatorView = container.querySelector('[data-calculator-view]');
            const resultView = container.querySelector('[data-result-view]');
            const skipOption = this._getSkipOption(container);

            // Hide others, then transition to calculator
            if (resultView) resultView.style.display = 'none';
            if (skipOption) skipOption.style.display = 'none';
            await this._transitionViews(modelsView, calculatorView);
            if (calculatorView) {
                this.isCalculatorView = true;
                this._updateHeader(container, 'calculator');
                this.renderCalculatorInputs(container);
                // ============================================================
                // BEGIN CUSTOM EDIT: Stape DL – pf_step_viewed | Date: 2026-03-20
                // What: Fire step viewed for calculator (once per session)
                // ============================================================
                this._dlLastStep = 'calculator';
                pfTrackStep(LG_SOURCE, 2, 'calculator', LG_STEP_TOTAL);
                // ============================================================
                // END CUSTOM EDIT
                // ============================================================
            }
        },

        // Hide calculator view, show models with View Transition
        async hideCalculatorView(container) {
            const modelsView = container.querySelector('[data-models-view]');
            const calculatorView = container.querySelector('[data-calculator-view]');
            const skipOption = this._getSkipOption(container);

            await this._transitionViews(calculatorView, modelsView, 'block', 'back');
            this._showView(skipOption, 'flex');
            this.isCalculatorView = false;
            this._updateHeader(container, 'models');
        },

        // ============================================================
        //  BEGIN CUSTOM EDIT: Dynamic header + mobile touch tooltips | Date: 2026-03-21
        //  What: Swap header text per view, make headers tap-to-show tooltip on mobile
        // ============================================================
        _updateHeader(container, mode) {
            const drawer = container.closest('x-drawer') || container.closest('.drawer');
            if (!drawer) return;
            const header = drawer.querySelector('[data-length-guide-header]');
            if (!header) return;

            const titleEl = header.querySelector('.product-finder-section-title');
            const descEl = header.querySelector('.product-finder-section-description');
            if (!titleEl || !descEl) return;

            // Cache original content on first call
            if (!this._originalHeaderTitle) {
                this._originalHeaderTitle = titleEl.textContent;
                this._originalHeaderDesc = descEl.textContent;
            }

            // Helper: build back-arrow title row
            const buildBackTitle = (title, onClick) => {
                titleEl.textContent = '';
                const backSpan = document.createElement('span');
                backSpan.className = 'calc-header-back';
                backSpan.style.cssText = 'cursor:pointer;display:inline-flex;align-items:center;gap:0.4rem';
                const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svg.setAttribute('width', '16');
                svg.setAttribute('height', '16');
                svg.setAttribute('viewBox', '0 0 24 24');
                svg.setAttribute('fill', 'none');
                svg.setAttribute('stroke', 'currentColor');
                svg.setAttribute('stroke-width', '2');
                const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                path.setAttribute('d', 'M19 12H5M12 19l-7-7 7-7');
                svg.appendChild(path);
                backSpan.appendChild(svg);
                backSpan.appendChild(document.createTextNode(title));
                titleEl.appendChild(backSpan);
                this._storeHandler(backSpan, 'click', onClick);
            };

            // Swap content with a coordinated fade
            const headerText = header.querySelector('.length-guide-header-text');
            const applyContent = () => {
                if (mode === 'calculator') {
                    const calcTitle = this._get(this, 'texts.calculatorTitle', 'Calculate Length');
                    buildBackTitle(calcTitle, () => this.hideCalculatorView(container));
                    descEl.textContent = this._get(this, 'texts.calculatorDescription', 'Find the perfect shoelace length for your shoes.');
                    drawer.classList.add('calculator-active');
                } else if (mode === 'result') {
                    const resultTitle = this._get(this, 'texts.resultTitle', 'Recommendation');
                    buildBackTitle(resultTitle, async () => {
                        const resultView = container.querySelector('[data-result-view]');
                        const calculatorView = container.querySelector('[data-calculator-view]');
                        await this._transitionViews(resultView, calculatorView, 'block', 'back');
                        this._updateHeader(container, 'calculator');
                    });
                    descEl.textContent = '';
                    drawer.classList.add('calculator-active');
                } else {
                    titleEl.textContent = this._originalHeaderTitle;
                    descEl.textContent = this._originalHeaderDesc;
                    drawer.classList.remove('calculator-active');
                }
            };

            // Animate: fade out → swap → fade in (synced with body transition)
            if (headerText) {
                headerText.style.transition = 'opacity 0.1s ease-out';
                headerText.style.opacity = '0';
                setTimeout(() => {
                    applyContent();
                    headerText.style.transition = 'opacity 0.15s ease-in';
                    headerText.style.opacity = '1';
                }, 100);
            } else {
                applyContent();
            }
        },
        // ============================================================
        //  END CUSTOM EDIT
        // ============================================================

        // ============================================================
        //  BEGIN CUSTOM EDIT: Slider Calculator UI | Date: 2026-03-21
        //  What: Rewrite renderCalculatorInputs with v2-style sliders
        // ============================================================
        // Get narrow/medium/wide label for a distance value
        _getDistanceLabel(value, type) {
            const texts = this._get(this.config, 'texts.calculator', {});
            const narrow = type === 'vertical'
                ? this._get(texts, 'verticalDistance.narrow.title', 'Narrow')
                : this._get(texts, 'horizontalDistance.narrow.title', 'Narrow');
            const medium = type === 'vertical'
                ? this._get(texts, 'verticalDistance.medium.title', 'Medium')
                : this._get(texts, 'horizontalDistance.medium.title', 'Medium');
            const wide = type === 'vertical'
                ? this._get(texts, 'verticalDistance.wide.title', 'Wide')
                : this._get(texts, 'horizontalDistance.wide.title', 'Wide');

            if (type === 'vertical') {
                if (value <= 1) return `(${narrow})`;
                if (value <= 2) return `(${medium})`;
                return `(${wide})`;
            } else {
                if (value <= 2.5) return `(${narrow})`;
                if (value <= 5) return `(${medium})`;
                return `(${wide})`;
            }
        },

        // Render calculator inputs — slider-based UI matching product finder v2
        renderCalculatorInputs(container) {
            const inputsContainer = container.querySelector('[data-calculator-inputs]');
            if (!inputsContainer) return;

            // Scope calculator to .length-guide-calculator for CSS
            const calcView = container.querySelector('[data-calculator-view]');
            if (calcView && !calcView.classList.contains('length-guide-calculator')) {
                calcView.classList.add('length-guide-calculator');
            }

            const eyeletsLabel = this._get(this, 'texts.options.number_of_eylets', 'Number of Eyelet Pairs');
            const verticalLabel = this._get(this, 'texts.options.vertical_distance', this._isImperial() ? 'Vertical Distance (in)' : 'Vertical Distance (cm)');
            const horizontalLabel = this._get(this, 'texts.options.horizontal_distance', this._isImperial() ? 'Horizontal Distance (in)' : 'Horizontal Distance (cm)');
            const calcTexts = this._get(this.config, 'texts.calculator', {});
            const eyeletTexts = this._get(calcTexts, 'eyelets', {});
            const pairSingular = eyeletTexts.pair || 'Pair';
            const pairPlural = eyeletTexts.pairs || 'Pairs';

            // Set initial defaults if not set
            if (!this.calculatorInputs.eyelets) this.calculatorInputs.eyelets = 7;
            if (!this.calculatorInputs.verticalDistance) this.calculatorInputs.verticalDistance = 1.5;
            if (!this.calculatorInputs.horizontalDistance) this.calculatorInputs.horizontalDistance = 5;

            const currentEyelets = this.calculatorInputs.eyelets;
            const currentVertical = this.calculatorInputs.verticalDistance;
            const currentHorizontal = this.calculatorInputs.horizontalDistance;

            // Helper: tooltip icon
            const renderTooltipIcon = (key) => {
                const imageUrl = this.tooltipImages?.[key];
                const textContent = this.tooltipContent?.[key];
                if (!imageUrl && (!textContent || textContent.trim() === '')) return '';
                let contentHtml = '';
                if (imageUrl) contentHtml += `<img src="${imageUrl}" alt="" class="tooltip-image" loading="lazy">`;
                if (textContent && textContent.trim() !== '') contentHtml += `<div class="tooltip-text">${textContent}</div>`;
                return `<span class="tooltip-wrapper">
                    <button type="button" class="tooltip-icon" aria-label="Show tooltip">${this.TOOLTIP_ICON_SVG}</button>
                    <div class="tooltip-content">${contentHtml}</div>
                </span>`;
            };

            // Helper: format eyelet display value
            const formatEyelets = (v) => `${v} ${v === 1 ? pairSingular : pairPlural}`;

            // Helper: format distance display (cm or imperial) + narrow/medium/wide label
            const formatVertical = (v) => {
                const formatted = this._formatSliderCm(v);
                const label = this._getDistanceLabel(v, 'vertical');
                return `${formatted} ${label}`;
            };
            const formatHorizontal = (v) => {
                const formatted = this._formatSliderCm(v);
                const label = this._getDistanceLabel(v, 'horizontal');
                return `${formatted} ${label}`;
            };

            // Helper: generate slider gradient background CSS
            const sliderGradient = (val, min, max) => {
                const pct = ((val - min) / (max - min)) * 100;
                return `background: linear-gradient(to right, rgb(var(--color-dark)) 0%, rgb(var(--color-dark)) ${pct}%, var(--pf-bg-secondary) ${pct}%, var(--pf-bg-secondary) 100%);`;
            };

            // Helper: render micro tick indicators on the track
            const renderMicroTicks = (min, max, step, majorSteps) => {
                const majorSet = new Set(majorSteps);
                let html = '';
                for (let v = min; v <= max + step * 0.01; v += step) {
                    const rounded = Math.round(v * 100) / 100;
                    const pct = ((rounded - min) / (max - min)) * 100;
                    const isMajor = majorSet.has(rounded);
                    html += `<span class="lg-slider-micro-tick${isMajor ? ' lg-slider-micro-tick--major' : ''}" style="left:${pct}%"></span>`;
                }
                return `<div class="lg-slider-micro-ticks">${html}</div>`;
            };

            // Helper: render tick labels with absolute positioning (matches micro tick alignment)
            const renderTicks = (ticks, min, max, majorSet, allMajor = false) => {
                return ticks.map(t => {
                    const pct = ((t - min) / (max - min)) * 100;
                    const isMajor = allMajor || majorSet.has(t);
                    const cls = isMajor ? 'lg-slider-tick lg-slider-tick--major' : 'lg-slider-tick';
                    const label = this._isImperial() && !allMajor ? (t * 0.393701).toFixed(1) + '\u2033' : t;
                    return `<span class="${cls}" style="left:${pct}%">${label}</span>`;
                }).join('');
            };

            // Eyelets ticks: 1-15, all same weight (no faded labels)
            const eyeletTicks = Array.from({ length: 15 }, (_, i) => i + 1);
            const eyeletTicksHtml = renderTicks(eyeletTicks, 1, 15, new Set(), true);

            // Vertical distance ticks: 0.5-3 step 0.5, major at 0.5, 1.5, 3
            const vertTicks = [0.5, 1, 1.5, 2, 2.5, 3];
            const vertMajors = new Set([0.5, 1.5, 3]);
            const vertTicksHtml = renderTicks(vertTicks, 0.5, 3, vertMajors);

            // Horizontal distance ticks: 1-8, major at 1, 4, 8
            const horizTicks = [1, 2, 3, 4, 5, 6, 7, 8];
            const horizMajors = new Set([1, 4, 8]);
            const horizTicksHtml = renderTicks(horizTicks, 1, 8, horizMajors);

            const isAdvanced = this.calculatorMode === 'advanced';
            const distanceDisplay = isAdvanced ? '' : 'style="display:none"';

            // Simple mode: use fixed defaults for V/H (only eyelets slider shown)
            if (!isAdvanced) {
                this.calculatorInputs.verticalDistance = 2;
                this.calculatorInputs.horizontalDistance = 3;
            }

            const html = `
                <div class="lg-calc-item">
                    <div class="lg-calc-item-header">
                        <span>${eyeletsLabel}</span>${renderTooltipIcon('number_of_eylets')}
                        <span class="lg-calc-selected-value" data-eyelets-label>${formatEyelets(currentEyelets)}</span>
                    </div>
                    <div class="lg-slider-container">
                        <input
                            type="range"
                            class="lg-slider"
                            data-slider="eyelets"
                            min="1" max="15" step="1"
                            value="${currentEyelets}"
                            style="${sliderGradient(currentEyelets, 1, 15)}"
                            aria-label="${eyeletsLabel}"
                        >
                        ${renderMicroTicks(1, 15, 1, [1, 5, 10, 15])}
                        <div class="lg-slider-ticks">${eyeletTicksHtml}</div>
                    </div>
                </div>

                <div class="lg-calc-item" ${distanceDisplay}>
                    <div class="lg-calc-item-header">
                        <span>${verticalLabel}</span>${renderTooltipIcon('vertical_distance')}
                        <span class="lg-calc-selected-value" data-vertical-label>${formatVertical(currentVertical)}</span>
                    </div>
                    <div class="lg-slider-container">
                        <input
                            type="range"
                            class="lg-slider"
                            data-slider="verticalDistance"
                            min="0.5" max="3" step="0.1"
                            value="${currentVertical}"
                            style="${sliderGradient(currentVertical, 0.5, 3)}"
                            aria-label="${verticalLabel}"
                        >
                        ${renderMicroTicks(0.5, 3, 0.1, [0.5, 1, 1.5, 2, 2.5, 3])}
                        <div class="lg-slider-ticks">${vertTicksHtml}</div>
                    </div>
                </div>

                <div class="lg-calc-item" ${distanceDisplay}>
                    <div class="lg-calc-item-header">
                        <span>${horizontalLabel}</span>${renderTooltipIcon('horizontal_distance')}
                        <span class="lg-calc-selected-value" data-horizontal-label>${formatHorizontal(currentHorizontal)}</span>
                    </div>
                    <div class="lg-slider-container">
                        <input
                            type="range"
                            class="lg-slider"
                            data-slider="horizontalDistance"
                            min="1" max="8" step="0.5"
                            value="${currentHorizontal}"
                            style="${sliderGradient(currentHorizontal, 1, 8)}"
                            aria-label="${horizontalLabel}"
                        >
                        ${renderMicroTicks(1, 8, 0.5, [1, 2, 3, 4, 5, 6, 7, 8])}
                        <div class="lg-slider-ticks">${horizTicksHtml}</div>
                    </div>
                </div>
            `;

            // NOTE: innerHTML is safe here — all values are from config (this._get)
            // or static strings/numbers. No user-supplied data is interpolated.
            inputsContainer.innerHTML = html;
            this.setupCalculatorInputHandlers(container);
            this.setupTooltipHandlers(inputsContainer);
            this.updateCalculateButtonState(container);

            // Trigger initial auto-calculate with defaults
            this.autoCalculateIfReady(container);
        },
        // ============================================================
        //  END CUSTOM EDIT
        // ============================================================

        // Setup calculator input handlers
        setupCalculatorInputHandlers(container) {
            // ============================================================
            //  BEGIN CUSTOM EDIT: Slider input handlers | Date: 2026-03-21
            //  What: Wire range sliders for eyelets/vertical/horizontal inputs
            // ============================================================
            const sliders = container.querySelectorAll('[data-slider]');
            sliders.forEach(slider => {
                const sliderName = slider.dataset.slider;

                const updateSlider = () => {
                    const rawVal = parseFloat(slider.value);
                    const min = parseFloat(slider.min);
                    const max = parseFloat(slider.max);
                    const pct = ((rawVal - min) / (max - min)) * 100;
                    slider.style.background = `linear-gradient(to right, rgb(var(--color-dark)) 0%, rgb(var(--color-dark)) ${pct}%, var(--pf-bg-secondary) ${pct}%, var(--pf-bg-secondary) 100%)`;

                    // Update state
                    this.calculatorInputs[sliderName] = rawVal;

                    // Update header label
                    // Map slider name to label data attribute
                    const labelAttrMap = {
                        eyelets: 'data-eyelets-label',
                        verticalDistance: 'data-vertical-label',
                        horizontalDistance: 'data-horizontal-label'
                    };
                    const labelEl = container.querySelector(`[${labelAttrMap[sliderName] || ('data-' + sliderName + '-label')}]`);
                    if (labelEl) {
                        if (sliderName === 'eyelets') {
                            const calcTexts = this._get(this.config, 'texts.calculator', {});
                            const eyeletTexts = this._get(calcTexts, 'eyelets', {});
                            const pairSingular = eyeletTexts.pair || 'Pair';
                            const pairPlural = eyeletTexts.pairs || 'Pairs';
                            labelEl.textContent = `${rawVal} ${rawVal === 1 ? pairSingular : pairPlural}`;
                        } else {
                            const formatted = this._formatSliderCm(rawVal);
                            const type = sliderName === 'verticalDistance' ? 'vertical' : 'horizontal';
                            const distLabel = this._getDistanceLabel(rawVal, type);
                            labelEl.textContent = `${formatted} ${distLabel}`;
                        }
                    }

                    this.autoCalculateIfReady(container);
                };

                this._storeHandler(slider, 'input', updateSlider);
            });
            // ============================================================
            //  END CUSTOM EDIT
            // ============================================================

            // Option item selection handlers (kept for radio-based fallback / mode toggle compatibility)
            const inputGroups = container.querySelectorAll('[data-input]');
            inputGroups.forEach(group => {
                const inputName = group.dataset.input;
                const items = group.querySelectorAll('.radio-option-no-image-item');
                const optionSelector = group.closest('.option-selector');
                // Custom input wrapper is now a sibling of the radio-option-values, not inside it
                const customInputWrapper = optionSelector?.querySelector(`[data-custom-input-wrapper="${inputName}"]`);
                const customInput = customInputWrapper?.querySelector(`[data-custom-input="${inputName}"]`);

                items.forEach(item => {
                    this._storeHandler(item, 'click', (e) => {
                        // Prevent click from bubbling when clicking on the input itself
                        if (e.target.tagName === 'INPUT') return;

                        // Update selection
                        items.forEach(i => i.classList.remove('selected'));
                        item.classList.add('selected');

                        // Check if custom option was selected
                        const isCustom = item.dataset.custom === 'true';

                        if (isCustom) {
                            // Show custom input wrapper below options
                            if (customInputWrapper) {
                                customInputWrapper.style.display = 'block';
                            }
                            if (customInput) {
                                customInput.focus();
                            }
                            // Mark as custom mode
                            this.calculatorInputs[`${inputName}Custom`] = true;
                            // Use custom value if available, otherwise null
                            const customValue = customInput?.value ? Number(customInput.value) : null;
                            this.calculatorInputs[inputName] = customValue;
                            this.calculatorInputs[`${inputName}Value`] = customInput?.value || '';

                            // Update label
                            const labelSpan = optionSelector?.querySelector('.selected-value');
                            if (labelSpan) {
                                const customLabel = this._get(this, 'texts.calculator.custom_badge', 'Custom');
                                labelSpan.textContent = customValue ? this._formatSliderCm(customValue) : customLabel;
                            }
                        } else {
                            // Hide custom input wrapper
                            if (customInputWrapper) {
                                customInputWrapper.style.display = 'none';
                            }
                            // Clear custom mode
                            this.calculatorInputs[`${inputName}Custom`] = false;
                            this.calculatorInputs[`${inputName}Value`] = '';

                            // Store value
                            const value = Number(item.dataset.value);
                            this.calculatorInputs[inputName] = value;

                            // Update label
                            const labelSpan = optionSelector?.querySelector('.selected-value');
                            if (labelSpan) {
                                const opt = this.getOptionByValue(inputName, value);
                                labelSpan.textContent = opt ? opt.title : value;
                            }
                        }

                        this.updateCalculateButtonState(container);

                        // Auto-calculate when all required inputs are filled
                        this.autoCalculateIfReady(container);
                    });
                });

                // Custom input field handler
                if (customInput) {
                    const minVal = parseFloat(customInput.min) || 0;
                    const maxVal = parseFloat(customInput.max) || 100;

                    // Clamp and update value helper
                    const updateValue = (clampNow = false) => {
                        let value = customInput.value ? Number(customInput.value) : null;

                        // Clamp value to min/max if requested
                        if (clampNow && value !== null) {
                            if (value < minVal) value = minVal;
                            if (value > maxVal) value = maxVal;
                            customInput.value = value;
                        }

                        this.calculatorInputs[inputName] = value;
                        this.calculatorInputs[`${inputName}Value`] = customInput.value || '';

                        // Update label
                        const labelSpan = optionSelector?.querySelector('.selected-value');
                        if (labelSpan) {
                            const customLabel = this._get(this, 'texts.calculator.custom_badge', 'Custom');
                            labelSpan.textContent = value ? this._formatSliderCm(value) : customLabel;
                        }

                        this.updateCalculateButtonState(container);
                        this.autoCalculateIfReady(container);
                    };

                    // On input - update state but don't clamp yet (allow typing)
                    this._storeHandler(customInput, 'input', () => updateValue(false));

                    // On blur - clamp value to limits
                    this._storeHandler(customInput, 'blur', () => updateValue(true));

                    // On change - clamp value (triggered after blur or on Enter)
                    this._storeHandler(customInput, 'change', () => updateValue(true));

                    // Prevent input click from deselecting
                    this._storeHandler(customInput, 'click', (e) => {
                        e.stopPropagation();
                    });
                }
            });

            // Mode toggle handler
            const modeToggle = container.querySelector('[data-calculator-mode-toggle]');
            if (modeToggle) {
                const modeButtons = modeToggle.querySelectorAll('[data-mode]');
                modeButtons.forEach(btn => {
                    this._storeHandler(btn, 'click', () => {
                        const mode = btn.dataset.mode;
                        if (mode === this.calculatorMode) return;

                        // Save current mode's inputs before switching
                        const currentInputsCopy = {
                            eyelets: this.calculatorInputs.eyelets,
                            verticalDistance: this.calculatorInputs.verticalDistance,
                            horizontalDistance: this.calculatorInputs.horizontalDistance,
                            verticalDistanceCustom: this.calculatorInputs.verticalDistanceCustom,
                            horizontalDistanceCustom: this.calculatorInputs.horizontalDistanceCustom,
                            verticalDistanceValue: this.calculatorInputs.verticalDistanceValue,
                            horizontalDistanceValue: this.calculatorInputs.horizontalDistanceValue
                        };

                        if (this.calculatorMode === 'simple') {
                            this.simpleModeSavedInputs = currentInputsCopy;
                        } else {
                            this.advancedModeSavedInputs = currentInputsCopy;
                        }

                        this.calculatorMode = mode;

                        // Restore saved inputs for the new mode, or use defaults
                        if (mode === 'simple') {
                            const saved = this.simpleModeSavedInputs;
                            if (saved) {
                                this.calculatorInputs.eyelets = saved.eyelets;
                                this.calculatorInputs.verticalDistance = saved.verticalDistance !== null ? saved.verticalDistance : 2;
                                this.calculatorInputs.horizontalDistance = saved.horizontalDistance !== null ? saved.horizontalDistance : 3;
                            } else {
                                // Defaults for simple mode (first time)
                                this.calculatorInputs.eyelets = null;
                                this.calculatorInputs.verticalDistance = 2;
                                this.calculatorInputs.horizontalDistance = 3;
                            }
                            this.calculatorInputs.verticalDistanceCustom = false;
                            this.calculatorInputs.horizontalDistanceCustom = false;
                            this.calculatorInputs.verticalDistanceValue = '';
                            this.calculatorInputs.horizontalDistanceValue = '';
                        } else {
                            const saved = this.advancedModeSavedInputs;
                            if (saved) {
                                this.calculatorInputs.eyelets = saved.eyelets;
                                this.calculatorInputs.verticalDistance = saved.verticalDistance;
                                this.calculatorInputs.horizontalDistance = saved.horizontalDistance;
                                this.calculatorInputs.verticalDistanceCustom = saved.verticalDistanceCustom || false;
                                this.calculatorInputs.horizontalDistanceCustom = saved.horizontalDistanceCustom || false;
                                this.calculatorInputs.verticalDistanceValue = saved.verticalDistanceValue || '';
                                this.calculatorInputs.horizontalDistanceValue = saved.horizontalDistanceValue || '';
                            } else {
                                // Defaults for advanced mode (first time) - nothing pre-selected
                                this.calculatorInputs.eyelets = null;
                                this.calculatorInputs.verticalDistance = null;
                                this.calculatorInputs.horizontalDistance = null;
                                this.calculatorInputs.verticalDistanceCustom = false;
                                this.calculatorInputs.horizontalDistanceCustom = false;
                                this.calculatorInputs.verticalDistanceValue = '';
                                this.calculatorInputs.horizontalDistanceValue = '';
                            }
                        }

                        // Hide match result when switching modes
                        const matchResult = container.querySelector('[data-match-result]');
                        if (matchResult) {
                            matchResult.style.display = 'none';
                        }

                        // Update slider position
                        const slider = modeToggle.querySelector('[data-slider]');
                        if (slider) {
                            slider.style.left = mode === 'advanced' ? 'calc(50%)' : '4px';
                        }

                        // Update button states
                        modeButtons.forEach(b => b.classList.toggle('active', b.dataset.mode === mode));

                        // Re-render inputs
                        this.renderCalculatorInputs(container);

                        // Update labels based on restored state
                        this.updateCalculatorLabels(container);

                        // Trigger auto-calculate if all inputs are filled
                        this.autoCalculateIfReady(container);
                    });
                });
            }

            // Confirm button handler (auto-calculate shows this when match found)
            const confirmBtn = container.querySelector('[data-confirm-match]');
            if (confirmBtn) {
                this._storeHandler(confirmBtn, 'click', () => this.confirmCalculatedMatch(container));
            }

            // Back button handler
            const backBtn = container.querySelector('[data-back-to-models]');
            if (backBtn) {
                this._storeHandler(backBtn, 'click', () => this.hideCalculatorView(container));
            }
        },

        // ============================================================
        //  BEGIN CUSTOM EDIT: Tooltip Handlers | Date: 2026-01-04
        // ============================================================
        // Setup hover/focus handlers for tooltip icons with smart positioning
        setupTooltipHandlers(container) {
            const tooltipIcons = container.querySelectorAll('.tooltip-icon');
            tooltipIcons.forEach(icon => {
                const tooltipContent = icon.nextElementSibling;
                if (!tooltipContent || !tooltipContent.classList.contains('tooltip-content')) return;

                let hoverTimeout = null;
                let isShowing = false;

                const showTooltip = () => {
                    // Clear any pending hide timeout
                    if (hoverTimeout) {
                        clearTimeout(hoverTimeout);
                        hoverTimeout = null;
                    }

                    // Skip if already visible
                    if (isShowing || tooltipContent.classList.contains('visible')) return;

                    // Add delay before showing (150ms)
                    hoverTimeout = setTimeout(() => {
                        isShowing = true;

                        // Get icon position
                        const iconRect = icon.getBoundingClientRect();
                        const modalRect = container.closest('.length-guide-modal')?.getBoundingClientRect();
                        const topBoundary = modalRect ? modalRect.top : 0;

                        // Prepare for measurement - hidden but rendered, NO transitions
                        tooltipContent.classList.remove('tooltip-below');
                        tooltipContent.style.cssText = 'opacity: 0; visibility: hidden; transition: none;';
                        tooltipContent.classList.add('visible');

                        // Force reflow and get dimensions
                        const tooltipHeight = tooltipContent.offsetHeight;
                        const tooltipWidth = tooltipContent.offsetWidth;

                        // Calculate position - try above first
                        let showBelow = false;
                        let topPos = iconRect.top - tooltipHeight - 12;

                        // If would clip at top of modal, show below instead
                        if (topPos < topBoundary + 5) {
                            topPos = iconRect.bottom + 12;
                            showBelow = true;
                            tooltipContent.classList.add('tooltip-below');
                        }

                        // Position horizontally centered on icon
                        const leftPos = iconRect.left + (iconRect.width / 2) - (tooltipWidth / 2);

                        // Apply fixed positioning WITHOUT transition first
                        const transformOrigin = showBelow ? 'center top' : 'center bottom';
                        tooltipContent.style.cssText = `
                            position: fixed;
                            top: ${topPos}px;
                            left: ${Math.max(10, leftPos)}px;
                            bottom: auto;
                            margin: 0;
                            transform: scale(0.9);
                            transform-origin: ${transformOrigin};
                            opacity: 0;
                            transition: none;
                        `;

                        // Force reflow to apply position
                        void tooltipContent.offsetHeight;

                        // Now add transition and animate
                        tooltipContent.style.transition = 'transform 0.15s ease, opacity 0.15s ease';

                        // Force another reflow before animating
                        void tooltipContent.offsetHeight;

                        tooltipContent.style.transform = 'scale(1)';
                        tooltipContent.style.opacity = '1';
                    }, 150);
                };

                const hideTooltip = () => {
                    // Clear any pending show timeout
                    if (hoverTimeout) {
                        clearTimeout(hoverTimeout);
                        hoverTimeout = null;
                    }

                    // If not showing, nothing to hide
                    if (!isShowing && !tooltipContent.classList.contains('visible')) return;

                    // Animate out (shrink back toward icon)
                    tooltipContent.style.transform = 'scale(0.9)';
                    tooltipContent.style.opacity = '0';

                    // After transition, clean up
                    setTimeout(() => {
                        isShowing = false;
                        tooltipContent.classList.remove('visible', 'tooltip-below');
                        tooltipContent.style.cssText = '';
                    }, 150); // Match CSS transition duration
                };

                this._storeHandler(icon, 'mouseenter', showTooltip);
                this._storeHandler(icon, 'mouseleave', hideTooltip);
                this._storeHandler(icon, 'focus', showTooltip);
                this._storeHandler(icon, 'blur', hideTooltip);

                // Mobile: make entire header row clickable to toggle tooltip
                const headerRow = icon.closest('.lg-calc-item-header');
                if (headerRow && 'ontouchstart' in window) {
                    this._storeHandler(headerRow, 'click', (e) => {
                        // Don't trigger if clicking the tooltip icon itself (already handled)
                        if (e.target.closest('.tooltip-icon')) return;
                        if (isShowing || tooltipContent.classList.contains('visible')) {
                            hideTooltip();
                        } else {
                            showTooltip();
                        }
                    });
                }

                // Mobile: tap anywhere outside to dismiss
                if ('ontouchstart' in window) {
                    this._storeHandler(document, 'click', (e) => {
                        if (!isShowing && !tooltipContent.classList.contains('visible')) return;
                        if (e.target.closest('.tooltip-icon') || e.target.closest('.tooltip-content') || e.target.closest('.lg-calc-item-header')) return;
                        hideTooltip();
                    });
                }
            });
        },
        // ============================================================
        //  END CUSTOM EDIT
        // ============================================================

        // Get option object by input name and value
        getOptionByValue(inputName, value) {
            const stepOptions = this.getLengthStepOptions();
            const optionsMap = {
                'verticalDistance': stepOptions.verticalDistance,
                'horizontalDistance': stepOptions.horizontalDistance,
                'eyelets': stepOptions.numberOfEylets
            };
            const options = optionsMap[inputName];
            return options?.find(opt => opt.value === value);
        },

        // Update all labels based on current calculator inputs state
        updateCalculatorLabels(container) {
            const pairSingular = this._get(this.config, 'texts.calculator.eyelets.pair', 'pair');
            const pairPlural = this._get(this.config, 'texts.calculator.eyelets.pairs', 'pairs');

            // Update eyelets label
            const eyeletsLabel = container.querySelector('[data-eyelets-label]');
            if (eyeletsLabel) {
                const v = this.calculatorInputs.eyelets;
                eyeletsLabel.textContent = v ? `${v} ${v === 1 ? pairSingular : pairPlural}` : '';
            }

            // Update vertical distance label
            const verticalLabel = container.querySelector('[data-vertical-label]');
            if (verticalLabel) {
                const v = this.calculatorInputs.verticalDistance;
                if (v !== null && v !== undefined) {
                    verticalLabel.textContent = `${this._formatSliderCm(v)} ${this._getDistanceLabel(v, 'vertical')}`;
                } else {
                    verticalLabel.textContent = '';
                }
            }

            // Update horizontal distance label
            const horizontalLabel = container.querySelector('[data-horizontal-label]');
            if (horizontalLabel) {
                const v = this.calculatorInputs.horizontalDistance;
                if (v !== null && v !== undefined) {
                    horizontalLabel.textContent = `${this._formatSliderCm(v)} ${this._getDistanceLabel(v, 'horizontal')}`;
                } else {
                    horizontalLabel.textContent = '';
                }
            }
        },

        // Auto-calculate when all required inputs are filled
        // Shows matched variant length + Confirm button (not calculated length)
        async autoCalculateIfReady(container) {
            // ============================================================
            // BEGIN CUSTOM EDIT: V2 Calculator Lazy-Load | Date: 2026-03-21
            // What: Lazy-load v2 API variants before matching (replaces Shopify AJAX)
            // ============================================================
            if (!this._v2Variants && this._v2FormName && this._v2ColorRelation) {
                this._v2Variants = await this.fetchVariantsByFormAndColor(this._v2FormName, this._v2ColorRelation);
            }
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            const { eyelets, verticalDistance, horizontalDistance } = this.calculatorInputs;
            const matchResult = container.querySelector('[data-match-result]');
            const matchedLengthEl = container.querySelector('[data-matched-length]');
            const maxLengthWarning = container.querySelector('[data-max-length-warning]');
            const MAX_LENGTH = 240;

            if (eyelets > 0) { // Sliders always have verticalDistance + horizontalDistance set
                const calculatedLength = this.calculateLength();
                this.calculatedLength = calculatedLength;

                // Show/hide max length warning
                if (maxLengthWarning) {
                    maxLengthWarning.style.display = calculatedLength > MAX_LENGTH ? 'block' : 'none';
                }

                // Find matching variant within tolerance (uses Shopify product variants)
                const result = this.findVariantWithTolerance(Math.round(calculatedLength), this.productVariants, this.preservedAglet);

                if (result.variant) {
                    // Store match for confirm button
                    this.pendingMatch = {
                        variant: result.variant,
                        matchedLength: result.matchedLength,
                        isCustom: result.isCustom
                    };

                    // Show matched variant length (not calculated)
                    if (matchResult && matchedLengthEl) {
                        matchedLengthEl.textContent = this._formatLength(result.matchedLength);
                        matchResult.style.display = 'block';
                    }
                } else {
                    // No standard variant match - try custom length variant
                    const customVariant = this.findCustomLengthVariant();
                    if (customVariant) {
                        this.pendingMatch = {
                            variant: customVariant,
                            matchedLength: Math.round(calculatedLength),
                            isCustom: true
                        };

                        // Show calculated length
                        if (matchResult && matchedLengthEl) {
                            matchedLengthEl.textContent = this._formatLength(Math.round(calculatedLength));
                            matchResult.style.display = 'block';
                        }
                    } else {
                        // No match at all - hide result
                        this.pendingMatch = null;
                        if (matchResult) matchResult.style.display = 'none';
                    }
                }
            } else {
                // Not all inputs filled - hide match result
                this.pendingMatch = null;
                if (matchResult) matchResult.style.display = 'none';
            }
        },

        // Confirm the calculated match and show result view
        confirmCalculatedMatch(container) {
            if (!this.pendingMatch) return;

            const { variant, matchedLength, isCustom } = this.pendingMatch;
            this.showResultFromCalculator(container, variant, matchedLength, isCustom);
        },

        // Update UI state (legacy - kept for compatibility)
        updateCalculateButtonState() {
            // No longer needed - auto-calculate handles this
        },

        // Calculate length using Product Finder formula
        // Formula: length = (H + SQRT(H*H + V*V) * (P-1) + L) * 2
        // Where: P = pairs of eyelets, H = horizontal spacing, V = vertical spacing, L = 16cm
        calculateLength() {
            const P = this.calculatorInputs.eyelets || 0;
            const H = this.calculatorInputs.horizontalDistance || 5;
            const V = this.calculatorInputs.verticalDistance || 5;
            const L = 16; // Length of ends in cm

            if (P === 0) {
                return 0;
            }

            const sqrtValue = Math.sqrt(H * H + V * V);
            const calculatedLength = (H + sqrtValue * (P - 1) + L) * 2;

            return calculatedLength;
        },

        // Show calculated result with variant matching
        async showCalculatedResult(container, calculatedLength) {
            // ============================================================
            // BEGIN CUSTOM EDIT: V2 Calculator Lazy-Load (showCalc) | Date: 2026-03-21
            // What: Ensure v2 variants are loaded before matching in this path
            // ============================================================
            if (!this._v2Variants && this._v2FormName && this._v2ColorRelation) {
                this._v2Variants = await this.fetchVariantsByFormAndColor(this._v2FormName, this._v2ColorRelation);
            }
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            // Find variant within tolerance (prefers v2 API variants, falls back to Shopify AJAX)
            const result = this.findVariantWithTolerance(calculatedLength, this.productVariants, this.preservedAglet);

            if (result.variant) {
                // Show result view with matched variant
                await this.showResultFromCalculator(container, result.variant, result.matchedLength, result.isCustom);
            } else {
                // No match - use custom length variant
                const customVariant = this.findCustomLengthVariant();
                if (customVariant) {
                    await this.showResultFromCalculator(container, customVariant, calculatedLength, true);
                } else {
                    this.showError(container, this._get(this, 'texts.errors.noVariant', 'No matching variant found'));
                }
            }
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: V2 Calculator Variant Match | Date: 2026-03-21
        // What: Use v2 API variants (percentage tolerance) with Shopify AJAX fallback
        // ============================================================
        // Find variant within tolerance (Flow B - Calculator)
        findVariantWithTolerance(targetLength, variants, aglet, options = {}) {
            // Use v2 API variants if available (preferred), otherwise fall back to Shopify AJAX
            const v2 = this._v2Variants;

            if (v2 && v2.length > 0) {
                // V2 API path: percentage-based tolerance with explicit is_custom field
                const tolerance = 0.05; // 5% tolerance — prefer custom length over snapping to distant standard

                // Filter standard variants for the current aglet
                const normalizedAglet = this.normalizeString(aglet);
                const standard = v2.filter(v =>
                    this.normalizeString(v.option_1) === normalizedAglet && !v.is_custom && v.length_cm != null
                ).sort((a, b) => a.length_cm - b.length_cm);

                // Find custom variant for fallback
                const custom = v2.find(v => this.normalizeString(v.option_1) === normalizedAglet && v.is_custom === true);

                if (standard.length === 0) {
                    return custom
                        ? { variant: custom, matchedLength: targetLength, isCustom: true }
                        : { variant: null, matchedLength: targetLength, isCustom: true };
                }

                const maxAvailable = Math.max(...standard.map(v => v.length_cm));
                const MAX_CUSTOM_LENGTH = 180;

                // Try to find variant within tolerance
                const withinTolerance = standard.find(v => {
                    const percentDiff = Math.abs(v.length_cm - targetLength) / targetLength;
                    return percentDiff <= tolerance;
                });

                if (withinTolerance) {
                    // Standard variant match
                    return { variant: withinTolerance, matchedLength: withinTolerance.length_cm, isCustom: false };
                } else if (targetLength > maxAvailable && custom) {
                    // Exceeds max standard — use custom (capped at 180cm)
                    const capped = Math.min(targetLength, MAX_CUSTOM_LENGTH);
                    return { variant: custom, matchedLength: capped, isCustom: true };
                } else if (targetLength > maxAvailable && !custom) {
                    // Exceeds max, no custom — use highest standard
                    const highest = standard[standard.length - 1];
                    return { variant: highest, matchedLength: highest.length_cm, isCustom: false };
                } else if (options.preferClosestStandard || !custom) {
                    // Fixed recommendations should use the closest fixed variant rather than a custom fallback.
                    const closest = standard.reduce((prev, curr) =>
                        Math.abs(curr.length_cm - targetLength) < Math.abs(prev.length_cm - targetLength) ? curr : prev
                    );
                    return { variant: closest, matchedLength: closest.length_cm, isCustom: false };
                } else {
                    // No tolerance match, under max, custom available — use custom length
                    const capped = Math.min(targetLength, MAX_CUSTOM_LENGTH);
                    return { variant: custom, matchedLength: capped, isCustom: true };
                }
            }

            // Fallback: Shopify AJAX variants (original logic)
            const TOLERANCE_CM = 5;
            const minRange = targetLength - TOLERANCE_CM;
            const maxRange = targetLength + TOLERANCE_CM;

            if (!variants || variants.length === 0) {
                return { variant: null, matchedLength: targetLength, isCustom: true };
            }

            const filtered = variants.filter(v => this.normalizeString(v.option1) === this.normalizeString(aglet));
            const withLengths = filtered
                .map(v => ({ ...v, numericLength: this.extractNumericLength(v.option2) }))
                .filter(v => v.numericLength !== null && !this.isCustomLengthValue(v.option2, v.sku));

            const inRange = withLengths.filter(v => v.numericLength >= minRange && v.numericLength <= maxRange);

            if (inRange.length === 0) {
                if (options.preferClosestStandard && withLengths.length > 0) {
                    const closestStandard = withLengths.reduce((prev, curr) =>
                        Math.abs(curr.numericLength - targetLength) < Math.abs(prev.numericLength - targetLength) ? curr : prev
                    );
                    return { variant: closestStandard, matchedLength: closestStandard.numericLength, isCustom: false };
                }

                return { variant: null, matchedLength: targetLength, isCustom: true };
            }

            const closest = inRange.reduce((prev, curr) => {
                const prevDiff = Math.abs(prev.numericLength - targetLength);
                const currDiff = Math.abs(curr.numericLength - targetLength);
                return currDiff < prevDiff ? curr : prev;
            });

            return { variant: closest, matchedLength: closest.numericLength, isCustom: false };
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        // Extract numeric length from option value
        extractNumericLength(value) {
            if (!value) return null;
            const str = String(value).replace(/[^\d.]/g, '');
            const num = parseFloat(str);
            return isNaN(num) ? null : num;
        },

        // Normalize string for comparison
        normalizeString(val) {
            if (!val) return '';
            return String(val).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        },

        getVariantIdentifier(variant) {
            return variant ? (variant.variant_id || variant.id) : null;
        },

        getVariantLength(variant) {
            if (!variant) return null;
            return variant.length_cm || this.extractNumericLength(variant.option_2 || variant.option2);
        },

        findVariantById(variantId) {
            if (!variantId) return null;
            const sources = [this._v2Variants || [], this.productVariants || []];

            for (const variants of sources) {
                const match = variants.find(variant => String(this.getVariantIdentifier(variant)) === String(variantId));
                if (match) return match;
            }

            return null;
        },

        // Check if value is custom length
        isCustomLengthValue(value, sku) {
            // SKU-based detection: all custom length variants have "-C-" in SKU
            if (sku && String(sku).includes('-C-')) return true;
            // Fallback: translated option value check
            if (!value) return false;
            const lower = String(value).toLowerCase();
            return lower === 'wunschlänge' || lower === 'custom' || lower.includes('wunsch') || lower.includes('sur mesure') || lower.includes('mesure');
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: V2 Custom Length Variant | Date: 2026-03-21
        // What: Find custom length variant — prefer v2 API, fallback to Shopify AJAX
        // ============================================================
        findCustomLengthVariant() {
            // Prefer v2 API variants
            if (this._v2Variants && this._v2Variants.length > 0) {
                const normalizedAglet = this.normalizeString(this.preservedAglet);
                return this._v2Variants.find(v =>
                    this.normalizeString(v.option_1) === normalizedAglet && v.is_custom === true
                ) || null;
            }
            // Fallback: Shopify AJAX variants — use SKU-based detection (-C-)
            if (!this.productVariants || this.productVariants.length === 0) return null;
            return this.productVariants.find(v => {
                const matchAglet = this.normalizeString(v.option1) === this.normalizeString(this.preservedAglet);
                const isCustom = this.isCustomLengthValue(v.option2, v.sku);
                return matchAglet && isCustom;
            });
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        // Show result from calculator
        async showResultFromCalculator(container, variant, length, isCustom) {
            const calculatorView = container.querySelector('[data-calculator-view]');
            const resultView = container.querySelector('[data-result-view]');

            // Update header to result mode
            this._updateHeader(container, 'result');

            // Render result card first (so content is ready for transition)
            this.renderCalculatorResult(container, variant, length, isCustom);

            // Transition calculator → result
            await this._transitionViews(calculatorView, resultView);

            // ============================================================
            // BEGIN CUSTOM EDIT: Calculator Result Tracking | Date: 2026-02-05
            // What: Dispatch event for GA4 tracking when calculator shows result
            // ============================================================
            document.dispatchEvent(new CustomEvent('length-guide:calculator-result', {
                bubbles: true,
                detail: {
                    eyelet_pairs: this.calculatorInputs.eyelets || 0,
                    horizontal_distance: this.calculatorInputs.horizontalDistance || 0,
                    vertical_distance: this.calculatorInputs.verticalDistance || 0,
                    end_length: 16,
                    recommended_length: Math.round(length),
                    unit: this._isImperial() ? 'in' : 'cm'
                }
            }));
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            // ============================================================
            // BEGIN CUSTOM EDIT: Stape DL – pf_calculator_used | Date: 2026-03-20
            // What: Fire when calculator produces a result
            // ============================================================
            pfTrack('pf_calculator_used', LG_SOURCE, { mode: this.calculatorMode, eyelets: this.calculatorInputs.eyelets || 0, vertical_distance: this.calculatorInputs.verticalDistance || 0, horizontal_distance: this.calculatorInputs.horizontalDistance || 0, result_cm: Math.round(length) });
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================
        },

        // ============================================================
        // BEGIN CUSTOM EDIT: formatMoney helper | Date: 2025-12-28
        // What: Format price using Shopify money format
        // ============================================================
        _formatMoney(cents) {
            try {
                const moneyFormat = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '€{{amount}}';
                const amount = ((cents || 0) / 100).toFixed(2);
                let formatted = moneyFormat.replace(/\{\{\s*amount\s*\}\}/g, amount);
                formatted = formatted.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
                formatted = formatted.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100));
                formatted = formatted.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100));
                return formatted;
            } catch (e) {
                return '€' + ((cents || 0) / 100).toFixed(2);
            }
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        // ============================================================
        // BEGIN CUSTOM EDIT: B2B Price Tiers + Qty Selector | Date: 2026-04-13
        // What: Guard B2B-only modal UI, normalize quantity rules/tiers, and format API vs AJAX prices safely
        // ============================================================
        _b2bGetDefaults() {
            const b2b = this._get(this.config, 'b2b', {}) || {};
            return {
                enabled: !!b2b.enabled,
                labels: b2b.labels || {}
            };
        },
        _b2bToPositiveInt(value, fallback) {
            const parsed = parseInt(value, 10);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
        },
        _b2bGetQtyRule(variant) {
            const qr = variant && variant.quantity_rule;
            const min = this._b2bToPositiveInt(qr && (qr.minimum ?? qr.min), 1);
            const increment = this._b2bToPositiveInt(qr && qr.increment, 1);
            const maxValue = qr && (qr.maximum ?? qr.max);
            const max = maxValue == null || maxValue === '' ? null : this._b2bToPositiveInt(maxValue, null);

            return {
                min,
                increment,
                max: max != null && max >= min ? max : null
            };
        },
        _b2bNormalizeQuantity(value, rule) {
            const min = this._b2bToPositiveInt(rule && rule.min, 1);
            const increment = this._b2bToPositiveInt(rule && rule.increment, 1);
            const max = rule && rule.max != null ? this._b2bToPositiveInt(rule.max, null) : null;
            let quantity = this._b2bToPositiveInt(value, min);

            quantity = Math.max(quantity, min);

            if (increment > 1) {
                const remainder = (quantity - min) % increment;
                if (remainder !== 0) quantity += increment - remainder;
            }

            if (max != null && quantity > max) {
                quantity = max;
                if (increment > 1) {
                    const remainder = (quantity - min) % increment;
                    if (remainder !== 0) quantity -= remainder;
                }
                quantity = Math.max(quantity, min);
            }

            return quantity;
        },
        _b2bNormalizePriceTiers(tiers, baseMin) {
            if (!Array.isArray(tiers)) return [];
            const byMinimum = new Map();

            tiers.forEach(tier => {
                const minimum = this._b2bToPositiveInt(tier && (tier.min_qty ?? tier.minimum_quantity ?? tier.minimum), null);
                const price = tier && tier.price != null
                    ? parseFloat(tier.price)
                    : parseFloat(tier && tier.price_cents) / 100;
                const priceCents = tier && tier.price_cents != null ? parseFloat(tier.price_cents) : null;
                if (minimum == null || minimum === baseMin || !Number.isFinite(price)) return;
                byMinimum.set(minimum, { min_qty: minimum, price, price_cents: priceCents });
            });

            return Array.from(byMinimum.values()).sort((a, b) => a.min_qty - b.min_qty);
        },
        _b2bPriceToCents(value, variant) {
            const price = parseFloat(value || 0);
            if (!Number.isFinite(price)) return 0;
            return variant && variant.variant_id ? Math.round(price * 100) : Math.round(price);
        },
        _b2bTierPriceToCents(tier) {
            if (tier && tier.price_cents != null) return Math.round(parseFloat(tier.price_cents) || 0);
            const price = parseFloat(tier && tier.price);
            return Number.isFinite(price) ? Math.round(price * 100) : 0;
        },
        _b2bInterpolate(template, replacements) {
            if (!template) return '';
            let out = String(template);
            Object.keys(replacements).forEach(key => {
                out = out.replace(new RegExp('__' + key + '__', 'g'), String(replacements[key]));
            });
            return out;
        },
        _renderB2BSummary(variant, formId) {
            const defaults = this._b2bGetDefaults();
            if (!defaults.enabled || !variant) return '';

            const labels = defaults.labels;
            const rule = this._b2bGetQtyRule(variant);
            const { min, increment, max } = rule;

            let hint = '';
            if (increment > 1 && min === increment) {
                hint = this._b2bInterpolate(labels.soldInPacksOf, { QTY: increment });
            } else if (increment > 1) {
                hint = this._b2bInterpolate(labels.minAndIncrement, { MIN: min, STEP: increment });
            } else if (min > 1) {
                hint = this._b2bInterpolate(labels.minimumOrderOf, { QTY: min });
            }

            const tiers = this._b2bNormalizePriceTiers(variant.price_tiers || [], min);
            const hasBreaks = tiers.length > 0;

            const baseRowHtml = `
                <tr data-minimum-quantity="${min}">
                    <td>${this._b2bInterpolate(labels.minimum, { QTY: min })}</td>
                    <td class="text-end">${this._b2bInterpolate(labels.priceAtEach, { PRICE: this._formatMoney(this._b2bPriceToCents(variant.price, variant)) })}</td>
                </tr>
            `;
            const breakRowsHtml = tiers.map(t => `
                <tr data-minimum-quantity="${t.min_qty}">
                    <td>${this._b2bInterpolate(labels.minimum, { QTY: t.min_qty })}</td>
                    <td class="text-end">${this._b2bInterpolate(labels.priceAtEach, { PRICE: this._formatMoney(this._b2bTierPriceToCents(t)) })}</td>
                </tr>
            `).join('');

            const tiersBlock = hasBreaks ? `
                <details class="lg-b2b-tiers-wrap" open>
                    <summary class="lg-b2b-tiers-toggle">${labels.volumePricing || 'Volume pricing'}</summary>
                    <div class="b2b-tiers-wrap__card">
                        <volume-pricing-table class="block" form="${formId}">
                            <table class="table table--sm table--bordered">
                                <tbody>
                                    ${baseRowHtml}
                                    ${breakRowsHtml}
                                </tbody>
                            </table>
                        </volume-pricing-table>
                    </div>
                </details>
            ` : '';

            const maxAttr = (max != null && !isNaN(max)) ? ` max="${max}"` : '';

            return `
                <div class="b2b-product-summary${hasBreaks ? ' b2b-product-summary--with-tiers' : ''}" style="position:relative;">
                    <div class="product-info__quantity-selector b2b-qty-wrap">
                        <div class="form-control">
                            <label for="${formId}-quantity" class="block-label text-subdued">${labels.quantity || 'Quantity'}:</label>
                            <quantity-selector class="quantity-selector">
                                <button type="button" class="quantity-selector__button" aria-label="${labels.decrease || 'Decrease quantity'}">−</button>
                                <input
                                    id="${formId}-quantity"
                                    type="number"
                                    inputmode="numeric"
                                    class="quantity-selector__input"
                                    name="quantity"
                                    form="${formId}"
                                    value="${min}"
                                    step="${increment}"
                                    min="${min}"${maxAttr}
                                    autocomplete="off"
                                >
                                <button type="button" class="quantity-selector__button" aria-label="${labels.increase || 'Increase quantity'}">+</button>
                            </quantity-selector>
                        </div>
                        ${hint ? `<p class="text-subdued text-sm b2b-qty-wrap__hint">${hint}</p>` : ''}
                    </div>
                    ${tiersBlock}
                </div>
            `;
        },
        // ============================================================
        // BEGIN CUSTOM EDIT: Active tier highlight | Date: 2026-05-14
        // What: Mark the highest tier where current qty meets its minimum.
        //       Drives the accent bar + bg tint in LGM B2B refinement CSS.
        // ============================================================
        _b2bUpdateActiveTier(container, qty) {
            if (!container) return;
            const rows = container.querySelectorAll('.b2b-tiers-wrap__card tbody tr[data-minimum-quantity]');
            if (!rows.length) return;
            const numQty = parseInt(qty, 10);
            if (!Number.isFinite(numQty)) return;
            let activeMin = -Infinity;
            rows.forEach(r => {
                const tMin = parseInt(r.dataset.minimumQuantity, 10);
                if (Number.isFinite(tMin) && tMin <= numQty && tMin > activeMin) activeMin = tMin;
            });
            rows.forEach(r => {
                const tMin = parseInt(r.dataset.minimumQuantity, 10);
                if (tMin === activeMin) r.setAttribute('data-active', 'true');
                else r.removeAttribute('data-active');
            });
        },
        _b2bSetupActiveTier(rootContainer, formId) {
            const container = rootContainer && rootContainer.querySelector('.b2b-product-summary');
            if (!container) return;
            const input = formId
                ? document.querySelector(`input[name="quantity"][form="${formId}"]`)
                : container.querySelector('input[name="quantity"]');
            if (!input) return;
            const update = () => this._b2bUpdateActiveTier(container, input.value);
            update();
            input.addEventListener('input', update);
            input.addEventListener('change', update);
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================
        _readModalQuantity(formId, fallback, variant) {
            const input = formId ? document.querySelector(`input[name="quantity"][form="${formId}"]`) : null;
            if (!input) return fallback || 1;

            const inputRule = {
                min: this._b2bToPositiveInt(input.getAttribute('min'), 1),
                increment: this._b2bToPositiveInt(input.getAttribute('step'), 1),
                max: input.hasAttribute('max') ? this._b2bToPositiveInt(input.getAttribute('max'), null) : null
            };
            const rule = variant ? this._b2bGetQtyRule(variant) : inputRule;
            const normalized = this._b2bNormalizeQuantity(input.value, rule);
            input.value = normalized;
            return normalized;
        },
        // ============================================================
        // END CUSTOM EDIT
        // ============================================================

        // Render calculator result - matches shoe model result view exactly (no shoe size info)
        renderCalculatorResult(container, variant, length, isCustom) {
            const resultView = container.querySelector('[data-result-view]');
            const calculatorView = container.querySelector('[data-calculator-view]');
            const skipOption = this._getSkipOption(container);

            if (!resultView) return;

            const lengthLabel = this._get(this, 'texts.lengthLabel', 'Length');
            const productTitle = this._get(this.config, 'presetValues.productTitle', '');

            // Get variant image from variantImages map (keyed by option1/aglet value)
            let variantImageUrl = '';
            const variantImages = this._get(this.config, 'variantImages', {});

            if (this.preservedAglet && variantImages[this.preservedAglet]) {
                variantImageUrl = variantImages[this.preservedAglet];
            } else {
                variantImageUrl = this._get(this.config, 'presetValues.productImage', '');
            }

            // ============================================================
            // BEGIN CUSTOM EDIT: V2 Calculator Variant Props | Date: 2026-03-19
            // What: Handle both Shopify AJAX variant (price in cents) and v2 API variant
            // ============================================================
            // Shopify AJAX variants don't have form_name directly; leave empty for calculator flow
            const formName = variant.form_name || '';
            const formLabel = this._get(this, 'texts.formLabel', 'Form');

            // V2 API variants have variant_id + price in dollars (e.g. 17.99)
            // Shopify AJAX variants have id + price in cents (e.g. 1799)
            const isV2Variant = !!variant.variant_id;
            const priceInCents = isV2Variant ? Math.round((variant.price || 0) * 100) : (variant.price || 0);
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================
            const priceNote = this._get(this, 'texts.priceNote', 'inkl. MwSt. Kostenloser Versand ab 2 Paar');

            // BEGIN CUSTOM EDIT: B2B Form ID (calculator) | Date: 2026-04-11 | What: Unique form id for calculator result qty selector
            this._currentFormId = `lg-modal-form-${variant.variant_id || variant.id || 'unknown'}-${Date.now()}`;
            // END CUSTOM EDIT

            // Result view — back button handled by header, no duplicate needed
            resultView.innerHTML = `
                <div class="length-guide-result">
                    <div class="product-summary-card">
                        ${variantImageUrl ? `
                            <div class="product-summary-image-container">
                                <img src="${variantImageUrl}" alt="${productTitle}" class="product-summary-image" loading="lazy">
                            </div>
                        ` : ''}

                        <div class="product-summary-info">
                            ${productTitle ? `<div class="product-summary-title">${this._sanitize(productTitle)}</div>` : ''}

                            <div class="product-summary-detail-container">
                                ${formName ? `
                                    <div class="product-summary-detail">
                                        <span class="product-summary-detail-label">${formLabel}</span>
                                        <span class="product-summary-detail-value">${this._sanitize(formName)}</span>
                                    </div>
                                ` : ''}
                                ${this.preservedAglet ? `
                                    <div class="product-summary-detail">
                                        <span class="product-summary-detail-label">${this._get(this, 'texts.agletLabel', 'Aglet')}</span>
                                        <span class="product-summary-detail-value">${this._sanitize(this.preservedAglet)}</span>
                                    </div>
                                ` : ''}
                                <div class="product-summary-detail product-summary-detail--length">
                                    <span class="product-summary-detail-label">${lengthLabel}</span>
                                    <span class="product-summary-detail-value">${this._formatLength(length)}</span>
                                </div>
                            </div>

                            <!-- Price container (hidden for B2B — volume table already shows per-tier pricing) -->
                            ${!this._b2bGetDefaults().enabled && priceInCents > 0 ? `
                                <div class="product-summary-price-container">
                                    <span class="product-summary-price">${this._formatMoney(priceInCents)}</span>
                                    <span class="product-summary-price-note">${priceNote}</span>
                                </div>
                            ` : ''}
                            ${this._renderB2BSummary(variant, this._currentFormId)}

                            <div class="length-guide-result__actions">
                                <button type="button" class="length-guide-result__add-to-cart" data-add-to-cart-calc aria-label="${this._get(this, 'texts.addToCartFull', 'In den Warenkorb')}">
                                    ${this._get(this, 'texts.addToCartFull', 'In den Warenkorb')}
                                </button>
                            </div>

                            <!-- BEGIN CUSTOM EDIT: Gift Box below ATC (calculator flow) | Date: 2026-05-08 | What: Render gift-box checkbox below the ATC actions row; calculator addToCart() detects checkbox and switches to items[]/parent_id payload -->
                            ${this._getGiftBoxOptionHtml(this._currentFormId)}
                            <!-- END CUSTOM EDIT -->
                        </div>
                    </div>
                </div>
            `;

            // Hide calculator and skip option, show result with transition
            this._hideView(calculatorView);
            this._hideView(skipOption);
            this._showView(resultView, 'block');
            // BEGIN CUSTOM EDIT: Wire active-tier highlight | Date: 2026-05-14
            this._b2bSetupActiveTier(resultView, this._currentFormId);
            // END CUSTOM EDIT

            // ============================================================
            // BEGIN CUSTOM EDIT: Stape DL – pf_result_displayed | Date: 2026-03-20
            // What: Fire when calculator result view is shown
            // ============================================================
            this._dlLastStep = 'result';
            pfTrack('pf_result_displayed', LG_SOURCE, { variant_id: variant.id || variant.variant_id || '', shoe_model: '', shoelace_title: productTitle });
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            // Add to cart handler (pass button for loader state)
            const addToCartBtn = resultView.querySelector('[data-add-to-cart-calc]');
            if (addToCartBtn) {
                this._storeHandler(addToCartBtn, 'click', () => {
                    this.addCalculatedToCart(container, variant, length, isCustom, addToCartBtn);
                });
            }
        },

        // Add calculated length to cart (matches addToCart flow)
        async addCalculatedToCart(container, variant, length, isCustom, button) {
            const shopifyRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

            // Show loading state - use theme native button loader structure
            if (button) {
                button.disabled = true;
                button.setAttribute('aria-busy', 'true');
                const originalText = button.textContent;
                button.innerHTML = `
                    <div class="length-guide-btn-content">${originalText}</div>
                    <span class="button__loader">
                        <span></span>
                        <span></span>
                        <span></span>
                    </span>
                `;
            }

            let sectionsToBundle = ['variant-added'];
            document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
                bubbles: true,
                detail: { sections: sectionsToBundle }
            }));

            const properties = {
                '_Source': 'Length Guide Calculator',
                '_Calculator': 'true',
                '_Mode': this.calculatorMode === 'simple' ? 'Simple' : 'Expert',
                '_Eyelet_Pairs': this.calculatorInputs.eyelets || '',
                '_Vertical_Distance': this.calculatorInputs.verticalDistanceCustom ? this.calculatorInputs.verticalDistanceValue : (this.calculatorInputs.verticalDistance || ''),
                '_Horizontal_Distance': this.calculatorInputs.horizontalDistanceCustom ? this.calculatorInputs.horizontalDistanceValue : (this.calculatorInputs.horizontalDistance || '')
            };

            // Add custom length property if needed
            if (isCustom) {
                const customLengthLabel = this._get(this, 'texts.customLengthLabel', 'Custom Length');
                properties[customLengthLabel] = this._isImperial() ? this._cmToInches(length) : length;
            }

            // ============================================================
            // BEGIN CUSTOM EDIT: V2 Calculator Cart ID | Date: 2026-03-19
            // What: Use Shopify variant id (variant.id) for calculator flow
            // ============================================================
            // BEGIN CUSTOM EDIT: B2B Qty Read (calculator) | Date: 2026-04-13
            const modalQty = this._readModalQuantity(this._currentFormId, 1, variant);
            // END CUSTOM EDIT

            // ============================================================
            // BEGIN CUSTOM EDIT: Gift Box Detection (calculator flow) | Date: 2026-05-08
            // What: When gift-box checkbox is ticked, switch single cartData to items[]
            //       payload with parent_id so gift-box renders as nested cart line.
            // ============================================================
            const calcResultViewEl = container.querySelector('[data-result-view]');
            const calcGiftBoxInput = calcResultViewEl?.querySelector('input[data-gift-box-variant]:checked');
            const calcGiftBoxVariantId = calcGiftBoxInput ? parseInt(calcGiftBoxInput.dataset.giftBoxVariant, 10) : null;
            const calcParentVariantId = parseInt(variant.id || variant.variant_id, 10);

            let cartData;
            if (calcGiftBoxVariantId && Number.isFinite(calcGiftBoxVariantId) && Number.isFinite(calcParentVariantId)) {
                cartData = {
                    items: [
                        { id: calcParentVariantId, quantity: modalQty, properties: properties },
                        { id: calcGiftBoxVariantId, quantity: modalQty, parent_id: calcParentVariantId }
                    ],
                    sections: sectionsToBundle.join(','),
                    sections_url: window.location.pathname
                };
            } else {
                cartData = {
                    id: variant.id || variant.variant_id,
                    quantity: modalQty,
                    properties: properties,
                    sections: sectionsToBundle.join(','),
                    sections_url: window.location.pathname
                };
            }
            // ============================================================
            // END CUSTOM EDIT
            // ============================================================

            try {
                const response = await fetch(`${shopifyRoot}cart/add.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify(cartData)
                });

                if (!response.ok) {
                    throw new Error(`Cart add failed: ${response.status}`);
                }

                const responseJson = await response.json();

                // ============================================================
                // BEGIN CUSTOM EDIT: Stape DL – pf_add_to_cart | Date: 2026-03-20
                // What: Fire ATC event on calculator flow success
                // ============================================================
                this._dlAddedToCart = true;
                pfTrack('pf_add_to_cart', LG_SOURCE, { variant_id: variant.id || variant.variant_id || '', is_custom_length: !!isCustom, calculator_mode: this.calculatorMode, shoe_model: '' });
                // ============================================================
                // END CUSTOM EDIT
                // ============================================================

                // Fetch updated cart for cart:change event
                const cartResponse = await fetch(`${shopifyRoot}cart.js`);
                const cartContent = await cartResponse.json();

                // Merge sections from add response into cart content
                if (responseJson.sections) {
                    cartContent.sections = responseJson.sections;
                }

                // Close the length guide drawer and dispatch events AFTER it's fully closed
                const drawer = container.closest('x-drawer');
                if (drawer && drawer.hide) {
                    // Wait for drawer to fully hide before dispatching cart events
                    drawer.addEventListener('dialog:after-hide', () => {
                        // Dispatch variant:add event with items property (required by theme)
                        document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
                            bubbles: true,
                            detail: {
                                items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
                                cart: cartContent
                            }
                        }));

                        // Also dispatch cart:change for other listeners (cart count, etc.)
                        if (cartContent.sections && Object.keys(cartContent.sections).length > 1) {
                            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                                bubbles: true,
                                detail: {
                                    baseEvent: 'variant:add',
                                    cart: cartContent
                                }
                            }));
                        } else {
                            document.dispatchEvent(new CustomEvent('cart:refresh'));
                        }
                    }, { once: true });

                    drawer.hide();
                } else {
                    // Fallback: dispatch events immediately if no drawer
                    document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
                        bubbles: true,
                        detail: {
                            items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
                            cart: cartContent
                        }
                    }));
                }

                // Reset view in background (so it's ready for next open)
                setTimeout(() => {
                    this.resetView(container);
                }, 100);

            } catch (error) {
                // Restore button state on error
                if (button) {
                    button.disabled = false;
                    button.removeAttribute('aria-busy');
                    button.textContent = this._get(this, 'texts.addToCartFull', 'In den Warenkorb');
                }
                this.showError(container, this._get(this, 'texts.errors.cartAdd', 'Could not add to cart'));
            }
        }

        // ============================================================
        // END CUSTOM EDIT
        // ============================================================
    };

    // ============================================================
    // BEGIN CUSTOM EDIT: Fix event listener accumulation | Date: 2025-12-28
    // What: Remove duplicate listeners, add product:switched handler, add cleanup
    // ============================================================

    // Guard to prevent duplicate initialization
    let isInitialized = false;

    /**
     * Initialize when drawer opens
     */
    function initOnDrawerOpen() {
        // Prevent duplicate initialization
        if (isInitialized) return;
        isInitialized = true;

        // Single document-level listener for drawer open (no duplicate drawer-specific listener)
        document.addEventListener('dialog:after-show', (event) => {
            const drawer = event.target;
            if (drawer && drawer.hasAttribute && drawer.hasAttribute('data-length-guide-drawer')) {
                const container = drawer.querySelector('[data-length-guide-modal]');
                if (container) {
                    LengthGuideModal.init(container, window.lengthGuideConfig);
                    // ============================================================
                    // BEGIN CUSTOM EDIT: Stape DL – pf_started | Date: 2026-03-20
                    // What: Fire once per modal open session; reset tracking flags
                    // ============================================================
                    LengthGuideModal._dlAddedToCart = false;
                    LengthGuideModal._dlStepViewed = {};
                    LengthGuideModal._dlStartTime = Date.now();
                    LengthGuideModal._dlStarted = true;
                    LengthGuideModal._dlLastStep = 'model_selection';
                    if (typeof pfTrackStarted === 'function') pfTrackStarted(LG_SOURCE, 'modal');
                    // ============================================================
                    // END CUSTOM EDIT
                    // ============================================================
                }
            }
        });

        // Cleanup on drawer close to prevent stale state
        document.addEventListener('dialog:after-hide', (event) => {
            const drawer = event.target;
            if (drawer && drawer.hasAttribute && drawer.hasAttribute('data-length-guide-drawer')) {
                // ============================================================
                // BEGIN CUSTOM EDIT: Stape DL – pf_abandoned | Date: 2026-03-20
                // What: Fire if modal closes without ATC
                // ============================================================
                if (LengthGuideModal._dlStarted && !LengthGuideModal._dlAddedToCart) {
                    var elapsed = LengthGuideModal._dlStartTime ? Math.round((Date.now() - LengthGuideModal._dlStartTime) / 1000) : 0;
                    pfTrack('pf_abandoned', LG_SOURCE, { last_step_name: LengthGuideModal._dlLastStep || 'model_selection', time_seconds: elapsed });
                }
                LengthGuideModal._dlStarted = false;
                if (LengthGuideModal._dlSearchTimer) { clearTimeout(LengthGuideModal._dlSearchTimer); LengthGuideModal._dlSearchTimer = null; }
                // ============================================================
                // END CUSTOM EDIT
                // ============================================================

                // Cleanup handlers when drawer closes
                LengthGuideModal._cleanupHandlers();
                LengthGuideModal._clearCache();
            }
        });

        // Reset modal state when product is switched via AJAX
        document.addEventListener('product:switched', () => {
            LengthGuideModal.fullReset();
        });
    }

    // Initialize on DOM ready (with guard to prevent double calls)
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initOnDrawerOpen);
    } else {
        initOnDrawerOpen();
    }
    // ============================================================
    // END CUSTOM EDIT
    // ============================================================

    // Expose for manual initialization
    window.LengthGuideModal = LengthGuideModal;

})();

// ============================================================
// END CUSTOM EDIT
// ============================================================
